import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { createToyyibPayBill, verifyToyyibPayBillStatus, TOYYIBPAY_CONFIG } from '../../lib/toyyibpay';
import {
  getInvoicesDB,
  saveInvoiceDB,
  deleteInvoiceDB,
  saveTransactionDB,
  getMembersDB,
  getGelanggangDB,
  getPersatuanPaymentSettingsDB,
  DEFAULT_PERSATUAN_PAYMENT_SETTINGS
} from '../../services/db';
import { Modal } from '../../components/common/Modal';
import { PageHeader } from '../../components/common/PageHeader';
import { CardSkeleton } from '../../components/common/Skeleton';
import { InvoiceItem, InvoiceLineItem, Gelanggang, UserProfile, FinancialTransaction, GelanggangDetail, PersatuanPaymentSettings } from '../../types';
import { QrPaymentPageView } from '../../components/common/QrPaymentPageView';
import { uploadReceiptToCloudinary, resolveCloudinaryUrl } from '../../lib/cloudinary';
import { ReceiptViewerModal } from '../../components/receipts/ReceiptViewerModal';
import { saveViewScroll, restoreViewScroll } from '../../utils/scrollRestoration';
import {
  CreditCard,
  ShieldCheck,
  ArrowRight,
  Plus,
  Trash2,
  Check,
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  Printer,
  DollarSign,
  Users,
  ExternalLink,
  Receipt,
  RefreshCw,
  Building2,
  UserCheck,
  Share2,
  Pencil,
  ListPlus,
  ArrowUp,
  ArrowDown,
  Search,
  X,
  ArrowLeft,
  Upload,
  Eye,
  QrCode,
  Landmark,
  Wallet,
  ArrowUpDown,
  SlidersHorizontal,
  RotateCcw
} from 'lucide-react';
import { canUserAddChild, isSuperAdmin, hasRolePermission, sanitizeMemberId, isUserAbove18, getUserRoles } from '../../utils/formatters';
import { supportsBillingCycle, getBillingAmount, getYearlySavings, YEARLY_DISCOUNT_PERCENT, YEARLY_MONTHS, isEligibleForMonthlyFee, getGelanggangClassPrice, resolveMemberEnrollments, parseGelanggangClasses } from '../../utils/pricing';

export type InvoiceSortOption =
  | 'unpaid-first'
  | 'date-desc'
  | 'date-asc'
  | 'amount-desc'
  | 'amount-asc'
  | 'name-asc'
  | 'name-desc'
  | 'invoice-no';

export type PaymentBankAccountKey = 'all' | 'persatuan' | 'pusaka_warisan' | 'wadi_annour';

export function getInvoiceBankAccountKey(inv: InvoiceItem): 'persatuan' | 'pusaka_warisan' | 'wadi_annour' {
  if ((inv as any).bankAccount) {
    const raw = String((inv as any).bankAccount).toLowerCase();
    if (raw.includes('pusaka')) return 'pusaka_warisan';
    if (raw.includes('wadi')) return 'wadi_annour';
    if (raw.includes('persatuan') || raw.includes('asmm') || raw.includes('induk')) return 'persatuan';
  }

  // Persatuan categories (ROS official mandate)
  if (
    inv.category === 'Yuran Pendaftaran' ||
    inv.category === 'Yuran Bengkung' ||
    inv.category === 'Acara & Sijil' ||
    inv.category === 'Sumbangan' ||
    inv.payerType === 'Vendor / Pihak Ketiga'
  ) {
    return 'persatuan';
  }

  const g = (inv.gelanggang || '').trim().toLowerCase();
  const title = (inv.title || '').toLowerCase();
  const desc = (inv.description || '').toLowerCase();

  if (g.includes('wadi') || title.includes('wadi annour') || desc.includes('wadi annour')) {
    return 'wadi_annour';
  }

  if (g.includes('pusaka') || title.includes('pusaka warisan') || desc.includes('pusaka warisan')) {
    return 'pusaka_warisan';
  }

  if (g === 'asmm' || g === 'semua gelanggang') {
    return 'persatuan';
  }

  return 'persatuan';
}

export function isInvoicePaidToGelanggang(inv: InvoiceItem, userGelanggangName: string): boolean {
  if (!userGelanggangName) return false;
  const userG = userGelanggangName.trim().toLowerCase();
  if (userG === 'tiada' || userG === '') return false;

  const bankKey = getInvoiceBankAccountKey(inv);

  // If the bank key specifically routes to pusaka_warisan or wadi_annour:
  if (userG.includes('pusaka') && bankKey === 'pusaka_warisan') return true;
  if (userG.includes('wadi') && bankKey === 'wadi_annour') return true;

  // If the invoice is routed to persatuan, it is paid to Persatuan's payment method, NOT gelanggang
  if (bankKey === 'persatuan') return false;

  // Fallback match on gelanggang name
  const invG = (inv.gelanggang || '').trim().toLowerCase();
  if (userG.includes('pusaka') && invG.includes('pusaka')) return true;
  if (userG.includes('wadi') && invG.includes('wadi')) return true;
  if (invG === userG) return true;

  return false;
}

export function isInvoicePaidToPersatuan(inv: InvoiceItem): boolean {
  const bankKey = getInvoiceBankAccountKey(inv);
  return bankKey === 'persatuan';
}

export const PaymentPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const userGelanggang = currentUser?.gelanggang || 'Pusaka Warisan';
  const isSuper = Boolean(currentUser && isSuperAdmin(currentUser));
  const isExecutive = Boolean(isSuper || hasRolePermission(currentUser, ['Pengerusi', 'Timbalan Pengerusi', 'Setiausaha', 'Pen. Setiausaha', 'Bendahari']));
  const isGelanggangAdmin = Boolean(isSuper || hasRolePermission(currentUser, ['Ketua Gelanggang', 'Bendahari Gelanggang']));
  const isKetuaGelanggang = isGelanggangAdmin;
  const canGenerateInvoice = isExecutive || isGelanggangAdmin;

  // Helper to read initial status facet from URL (?status=unpaid, ?status=pending, ?status=paid)
  const getInitialFilterStatus = (): 'all' | 'unpaid' | 'pending' | 'paid' => {
    if (typeof window === 'undefined') return 'all';
    const params = new URLSearchParams(window.location.search);
    const s = (params.get('status') || params.get('tab') || '').toLowerCase().trim();
    if (s === 'unpaid' || s === 'belum_dibayar' || s === 'belum-dibayar' || s === 'belum') return 'unpaid';
    if (s === 'pending' || s === 'menunggu' || s === 'menunggu_pengesahan' || s === 'menunggu-pengesahan') return 'pending';
    if (s === 'paid' || s === 'telah_dibayar' || s === 'telah-dibayar' || s === 'lulus') return 'paid';
    return 'all';
  };

  const getInitialAccountKey = (): PaymentBankAccountKey => {
    if (typeof window === 'undefined') return 'all';
    const params = new URLSearchParams(window.location.search);
    const a = (params.get('account') || params.get('bank') || '').toLowerCase().trim();
    if (a === 'persatuan') return 'persatuan';
    if (a === 'pusaka_warisan' || a === 'pusaka-warisan' || a === 'pusaka') return 'pusaka_warisan';
    if (a === 'wadi_annour' || a === 'wadi-annour' || a === 'wadi') return 'wadi_annour';
    return 'all';
  };

  // Invoice Ledger State
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [filterStatus, setFilterStatus] = useState<'all' | 'unpaid' | 'pending' | 'paid'>(getInitialFilterStatus);
  const [sortBy, setSortBy] = useState<InvoiceSortOption>('unpaid-first');
  const [selectedAccountKey, setSelectedAccountKey] = useState<PaymentBankAccountKey>(getInitialAccountKey);
  const [persatuanSettings, setPersatuanSettings] = useState<PersatuanPaymentSettings>(DEFAULT_PERSATUAN_PAYMENT_SETTINGS);
  const [publicSearchQuery, setPublicSearchQuery] = useState<string>('');
  const [isProcessingId, setIsProcessingId] = useState<string | null>(null);
  const [isVerifyingId, setIsVerifyingId] = useState<string | null>(null);
  const [receiptData, setReceiptData] = useState<any>(null);
  const [printInvoiceData, setPrintInvoiceData] = useState<{ invoice: InvoiceItem; isPaid: boolean } | null>(null);
  const [singleInvoiceView, setSingleInvoiceView] = useState<InvoiceItem | null>(null);
  const [gelanggangList, setGelanggangList] = useState<GelanggangDetail[]>([]);
  const [activeQrInvoice, setActiveQrInvoice] = useState<InvoiceItem | null>(null);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<string>>(new Set());
  const [viewingReceiptUrl, setViewingReceiptUrl] = useState<string | null>(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState<boolean>(false);

  // Payment Verification State (Zero Modal Flow / Custom Confirmation)
  const [confirmingInvoice, setConfirmingInvoice] = useState<InvoiceItem | null>(null);
  const [isSubmittingSahkan, setIsSubmittingSahkan] = useState<boolean>(false);

  // Can current user confirm/verify (sahkan) this invoice payment?
  const canSahkanBayaran = (inv: InvoiceItem): boolean => {
    if (!currentUser) return false;
    if (inv.status === 'Lulus / Dibayar' || inv.status === 'Batal') return false;

    // Strict Rule: Hanya invois yang telah dimuat naik bukti resit sahaja boleh disahkan bayaran
    if (!inv.receiptUrl || inv.receiptUrl.trim() === '') {
      return false;
    }

    // Superadmin has universal verification bypass
    if (isSuper) return true;

    const userRoles = getUserRoles(currentUser).map(r => (r || '').toLowerCase().trim());

    // Top Persatuan Leadership: Pengerusi, Timbalan Pengerusi, Setiausaha, Pen. Setiausaha, Bendahari, Bendahari Kehormat
    const isPersatuanOfficer = userRoles.some(r =>
      [
        'pengerusi',
        'timbalan pengerusi',
        'setiausaha',
        'pen. setiausaha',
        'penolong setiausaha',
        'bendahari',
        'bendahari kehormat',
        'superadmin'
      ].includes(r)
    );

    const isPersatuanBanking = isInvoicePaidToPersatuan(inv);

    // Rule 1: Persatuan banking payments (Pendaftaran, Bengkung, Sijil, Sumbangan, Vendor, Akaun Persatuan)
    // Only verified by Persatuan personnel (Pengerusi, Setiausaha, Bendahari, etc.)
    if (isPersatuanBanking) {
      return isPersatuanOfficer;
    }

    // Rule 2: Gelanggang level payments (Yuran Bulanan, Akaun Gelanggang)
    // a) Gelanggang level personnel of the invoice's gelanggang
    const userG = (currentUser.gelanggang || userGelanggang || '').trim().toLowerCase();
    const invG = (inv.gelanggang || '').trim().toLowerCase();
    const bankKey = getInvoiceBankAccountKey(inv);

    const matchesGelanggang =
      userG === 'semua gelanggang' ||
      (userG.includes('wadi') && (invG.includes('wadi') || bankKey === 'wadi_annour')) ||
      (userG.includes('pusaka') && (invG.includes('pusaka') || bankKey === 'pusaka_warisan')) ||
      (userG.includes('paya') && invG.includes('paya')) ||
      (userG !== '' && userG !== 'tiada' && invG === userG);

    const isGelanggangPersonnel = userRoles.some(r =>
      [
        'ketua gelanggang',
        'penolong ketua gelanggang',
        'pen. ketua gelanggang',
        'bendahari gelanggang',
        'jurulatih',
        'jurulatih kanan',
        'jurulatih muda',
        'guru gelanggang',
        'setiausaha gelanggang',
        'ajk gelanggang',
        'ajk'
      ].includes(r)
    );

    if (isGelanggangPersonnel && matchesGelanggang) {
      return true;
    }

    // b) Top Persatuan leadership (Pengerusi, Setiausaha, Bendahari) have supreme oversight across all gelanggangs
    if (isPersatuanOfficer) {
      return true;
    }

    return false;
  };

  // Helper to get current month/year for dynamic actual data
  const getCurrentMonthYear = () => {
    const months = ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun', 'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'];
    const d = new Date();
    return `${months[d.getMonth()]} ${d.getFullYear()}`;
  };
  const defaultInvoiceTitle = `Yuran Bulanan Latihan Silat ${getCurrentMonthYear()}`;

  // New Invoice Modal State (Executive / Ketua Gelanggang / Vendor Invoicing)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [editingInvoice, setEditingInvoice] = useState<InvoiceItem | null>(null);
  const [payerType, setPayerType] = useState<'Ahli' | 'Vendor / Pihak Ketiga'>('Ahli');
  const [vendorName, setVendorName] = useState<string>('');
  const [vendorPhone, setVendorPhone] = useState<string>('');
  const [vendorEmail, setVendorEmail] = useState<string>('');
  const [vendorAddress, setVendorAddress] = useState<string>('');
  const [membersList, setMembersList] = useState<UserProfile[]>([]);
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [invCategory, setInvCategory] = useState<string>('Yuran Bulanan');
  const [invTitle, setInvTitle] = useState<string>(defaultInvoiceTitle);
  const [invDesc, setInvDesc] = useState<string>('');
  const [invAmount, setInvAmount] = useState<number>(40);
  const [invDueDate, setInvDueDate] = useState<string>('2026-08-25');
  const [invGelanggang, setInvGelanggang] = useState<Gelanggang>(isKetuaGelanggang ? userGelanggang : 'Wadi AnNour');
  const [billingCycle, setBillingCycle] = useState<'Bulanan' | 'Tahunan'>('Bulanan');
  const [invStatus, setInvStatus] = useState<InvoiceItem['status']>('Belum Dibayar');
  const [invReceiptUrl, setInvReceiptUrl] = useState<string>('');

  // Line items state for itemized invoice calculation
  const [lineItems, setLineItems] = useState<InvoiceLineItem[]>([
    { id: 'item-1', description: defaultInvoiceTitle, quantity: 1, unitPrice: 40, amount: 40 }
  ]);

  const handleAddLineItem = () => {
    const newItem: InvoiceLineItem = {
      id: `item-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      description: '',
      quantity: 1,
      unitPrice: 0,
      amount: 0
    };
    setLineItems(prev => [...prev, newItem]);
  };

  const handleUpdateLineItem = (index: number, field: keyof InvoiceLineItem, value: any) => {
    setLineItems(prev => {
      const updated = [...prev];
      const current = { ...updated[index], [field]: value };

      if (field === 'quantity' || field === 'unitPrice') {
        const qty = Number(current.quantity) || 0;
        const price = Number(current.unitPrice) || 0;
        current.amount = qty * price;
      }
      updated[index] = current;

      const total = updated.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
      setInvAmount(total);

      return updated;
    });
  };

  const handleRemoveLineItem = (index: number) => {
    setLineItems(prev => {
      if (prev.length <= 1) return prev;
      const updated = prev.filter((_, idx) => idx !== index);
      const total = updated.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
      setInvAmount(total);
      return updated;
    });
  };

  const handleMoveLineItemUp = (index: number) => {
    if (index <= 0) return;
    setLineItems(prev => {
      const updated = [...prev];
      const temp = updated[index - 1];
      updated[index - 1] = updated[index];
      updated[index] = temp;
      return updated;
    });
  };

  const handleMoveLineItemDown = (index: number) => {
    setLineItems(prev => {
      if (index >= prev.length - 1) return prev;
      const updated = [...prev];
      const temp = updated[index + 1];
      updated[index + 1] = updated[index];
      updated[index] = temp;
      return updated;
    });
  };

  const handleOpenEditModal = (inv: InvoiceItem) => {
    setEditingInvoice(inv);
    setPayerType(inv.payerType || (inv.memberId?.startsWith('VENDOR-') ? 'Vendor / Pihak Ketiga' : 'Ahli'));
    setVendorName(inv.memberName || '');
    setVendorPhone(inv.vendorPhone || inv.memberPhone || '');
    setVendorEmail(inv.vendorEmail || '');
    setVendorAddress(inv.vendorAddress || inv.address || '');
    setSelectedMemberId(inv.memberId || '');
    setInvCategory(inv.category || 'Yuran Bulanan');
    setInvTitle(inv.title || '');
    setInvDesc(inv.description || '');
    setInvAmount(inv.amount || 0);
    setInvDueDate(inv.dueDate || new Date().toISOString().split('T')[0]);
    setInvGelanggang(inv.gelanggang || 'Wadi AnNour');
    setInvStatus(inv.status || 'Belum Dibayar');
    setInvReceiptUrl(inv.receiptUrl || '');

    if (inv.items && inv.items.length > 0) {
      setLineItems(inv.items);
    } else {
      const fallbackAmount = Number(inv.amount) || 0;
      setLineItems([
        { id: `item-${Date.now()}`, description: inv.title || 'Perkhidmatan Utama', quantity: 1, unitPrice: fallbackAmount, amount: fallbackAmount }
      ]);
    }
    setIsCreateModalOpen(true);
  };

  const handleReceiptFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToast('error', 'Saiz Fail Terlalu Besar', 'Maksimum saiz fail resit adalah 5MB.');
      return;
    }

    setIsUploadingReceipt(true);
    showToast('info', 'Memuat Naik Resit...', file.name);

    uploadReceiptToCloudinary(file, editingInvoice?.invoiceNo || `inv-${Date.now()}`)
      .then(res => {
        setInvReceiptUrl(res.secureUrl);
        showToast('success', 'Resit Berjaya Disimpan!', file.name);
      })
      .catch(() => {
        const reader = new FileReader();
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            setInvReceiptUrl(reader.result);
            showToast('success', 'Resit Dimuat Naik Secara Tempatan', file.name);
          }
        };
        reader.readAsDataURL(file);
      })
      .finally(() => {
        setIsUploadingReceipt(false);
        e.target.value = '';
      });
  };

  useEffect(() => {
    // Read from persistent cache first for instant sub-second render
    const cached = localStorage.getItem('asmm_db_invoices_v4');
    if (cached) {
      setInvoices(JSON.parse(cached));
      setIsLoading(false);
    }

    // Silent background revalidation with Turso DB
    getInvoicesDB().then((data) => {
      setInvoices(data);
      setIsLoading(false);

      // Silent auto background revalidation for pending FPX invoices
      const pendingInvoices = data.filter(i => i.toyibBillCode && i.status === 'Menunggu Pengesahan');
      if (pendingInvoices.length > 0) {
        pendingInvoices.forEach(async (inv) => {
          try {
            const res = await verifyToyyibPayBillStatus(inv.toyibBillCode!);
            if (res.status === 'Disahkan') {
              const paidDate = res.paymentDate ? res.paymentDate.split('T')[0] : new Date().toISOString().split('T')[0];
              const updatedInvoice = {
                ...inv,
                status: 'Lulus / Dibayar' as const,
                paidAt: paidDate
              };
              const newInvoices = await saveInvoiceDB(updatedInvoice);
              setInvoices(newInvoices);

              // Record income transaction in Turso DB
              await saveTransactionDB({
                id: `ASMM-TX-${Date.now()}`,
                date: paidDate,
                category: inv.category,
                description: `Bayaran FPX Invois ${inv.invoiceNo}: ${inv.title} (${inv.toyibBillCode})`,
                amount: inv.amount,
                type: 'Income',
                payerOrPayee: inv.memberName,
                gelanggang: inv.gelanggang,
                recordedBy: 'Sistem FPX API Auto',
                status: 'Disahkan',
                toyibBillCode: inv.toyibBillCode
              });
            }
          } catch (err) {
            console.error("Auto verify invoice status failed:", err);
          }
        });
      }

      // Return URL query checker for ToyyibPay checkout redirect (Success: 1, Pending: 2, Failed: 3)
      const rawSearch = window.location.search || (window.location.hash.includes('?') ? window.location.hash.slice(window.location.hash.indexOf('?')) : '');
      const searchParams = new URLSearchParams(rawSearch);
      const statusId = searchParams.get('status_id') || searchParams.get('status') || searchParams.get('status_code');
      const billCodeParam = searchParams.get('billcode') || searchParams.get('billCode') || searchParams.get('bill_code') || searchParams.get('order_id');

      if (statusId || billCodeParam) {
        let matched = billCodeParam ? data.find(i => i.toyibBillCode === billCodeParam) : null;
        if (!matched && statusId === '1') {
          matched = data.find(i => i.status === 'Menunggu Pengesahan');
        }

        if (matched) {
          if (statusId === '1') {
            // SUCCESS: Immediately update to 'Lulus / Dibayar' & show Official Receipt
            const now = new Date();
            const paidDate = now.toISOString().split('T')[0];
            const paidTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
            const paidAtCombined = `${paidDate} ${paidTime}`;

            const updatedInvoice: InvoiceItem = {
              ...matched,
              status: 'Lulus / Dibayar',
              paidAt: paidAtCombined,
              paymentMethod: 'ToyyibPay FPX Online Banking'
            };

            saveInvoiceDB(updatedInvoice).then((newInvoices) => {
              setInvoices(newInvoices);

              // Record income transaction in Turso / local DB
              saveTransactionDB({
                id: `ASMM-TX-${Date.now()}`,
                date: paidDate,
                category: matched!.category,
                description: `Bayaran FPX Invois ${matched!.invoiceNo}: ${matched!.title} (${matched!.toyibBillCode})`,
                amount: matched!.amount,
                type: 'Income',
                payerOrPayee: matched!.memberName,
                gelanggang: matched!.gelanggang,
                recordedBy: currentUser?.name || 'Sistem FPX API',
                status: 'Disahkan',
                toyibBillCode: matched!.toyibBillCode
              });

              showToast('success', 'Bayaran FPX Lulus & Disahkan!', `Pembayaran FPX untuk Invois ${matched!.invoiceNo} (RM ${matched!.amount}) telah berjaya.`);

              // Display Official Receipt View directly
              setSingleInvoiceView(updatedInvoice);
            });
          } else if (statusId === '3') {
            // FAILED / CANCELLED: Reset status to 'Belum Dibayar'
            const resetInv: InvoiceItem = {
              ...matched,
              status: 'Belum Dibayar'
            };
            saveInvoiceDB(resetInv).then((newInvoices) => {
              setInvoices(newInvoices);
              showToast('error', 'Bayaran FPX Dibatalkan / Gagal', `Pembayaran FPX bagi Invois ${matched!.invoiceNo} tidak berjaya atau telah dibatalkan.`);
            });
          } else if (statusId === '2') {
            // PENDING: Keep as 'Menunggu Pengesahan'
            showToast('warning', 'Bayaran FPX Menunggu Pengesahan Bank', `Pembayaran FPX bagi Invois ${matched.invoiceNo} sedang disemak oleh portal perbankan.`);
          } else {
            // Fallback API check
            handleVerifyInvoiceStatus(matched);
          }

          // Clean URL parameters from address bar
          if (typeof window !== 'undefined' && window.history && window.history.replaceState) {
            const cleanUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);
          }
        }
      }

      // Direct URL query parameter for sharing invoices / receipts to 3rd party vendors (?inv=INV-2026-999)
      const invQuery = searchParams.get('inv') || searchParams.get('invoice');
      const payQuery = searchParams.get('pay');
      if (invQuery && !statusId && !billCodeParam) {
        const foundInv = data.find(i => i.invoiceNo.toLowerCase() === invQuery.toLowerCase() || i.id === invQuery);
        if (foundInv) {
          if (payQuery === 'qr') {
            setActiveQrInvoice(foundInv);
          } else {
            setSingleInvoiceView(foundInv);
          }
        }
      }
    }).catch(() => {
      setIsLoading(false);
    });

    getGelanggangDB().then(setGelanggangList).catch(() => { });
    getMembersDB().then(setMembersList).catch(() => { });
    getPersatuanPaymentSettingsDB().then(setPersatuanSettings).catch(() => { });

    const handleDbUpdate = () => {
      getInvoicesDB().then(setInvoices).catch(() => { });
      getGelanggangDB().then(setGelanggangList).catch(() => { });
      getPersatuanPaymentSettingsDB().then(setPersatuanSettings).catch(() => { });
    };
    window.addEventListener('asmm_db_updated', handleDbUpdate);
    return () => window.removeEventListener('asmm_db_updated', handleDbUpdate);
  }, [canGenerateInvoice, currentUser]);

  // Synchronize browser URL Back/Forward navigation with active invoice/receipt view & facet tabs
  useEffect(() => {
    const handlePopState = () => {
      const search = new URLSearchParams(window.location.search);
      const invParam = search.get('inv') || search.get('invoice');
      const payParam = search.get('pay');

      // Sync filter status facet from URL
      const s = (search.get('status') || search.get('tab') || '').toLowerCase().trim();
      if (s === 'unpaid' || s === 'belum_dibayar' || s === 'belum-dibayar' || s === 'belum') {
        setFilterStatus('unpaid');
      } else if (s === 'pending' || s === 'menunggu' || s === 'menunggu_pengesahan' || s === 'menunggu-pengesahan') {
        setFilterStatus('pending');
      } else if (s === 'paid' || s === 'telah_dibayar' || s === 'telah-dibayar' || s === 'lulus') {
        setFilterStatus('paid');
      } else {
        setFilterStatus('all');
      }

      // Sync account facet from URL
      const a = (search.get('account') || search.get('bank') || '').toLowerCase().trim();
      if (a === 'persatuan') {
        setSelectedAccountKey('persatuan');
      } else if (a === 'pusaka_warisan' || a === 'pusaka-warisan' || a === 'pusaka') {
        setSelectedAccountKey('pusaka_warisan');
      } else if (a === 'wadi_annour' || a === 'wadi-annour' || a === 'wadi') {
        setSelectedAccountKey('wadi_annour');
      } else {
        setSelectedAccountKey('all');
      }

      if (invParam) {
        const found = invoices.find(i => i.invoiceNo.toLowerCase() === invParam.toLowerCase() || i.id === invParam);
        if (found) {
          if (payParam === 'qr') {
            setActiveQrInvoice(found);
            setSingleInvoiceView(null);
          } else {
            setSingleInvoiceView(found);
            setActiveQrInvoice(null);
          }
          return;
        }
      }

      setSingleInvoiceView(null);
      setActiveQrInvoice(null);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [invoices]);

  // Auto-calculate line items for monthly fees based on member's active enrollments
  useEffect(() => {
    // CRITICAL: Do NOT overwrite lineItems or amounts when editing an existing invoice!
    if (editingInvoice) return;

    if (payerType === 'Ahli' && selectedMemberId && invCategory === 'Yuran Bulanan') {
      const targetMember = membersList.find(m => m.id === selectedMemberId);
      if (targetMember) {
        const isAdult = isUserAbove18(targetMember.icNo);
        const enrollments = resolveMemberEnrollments(targetMember, gelanggangList);

        if (enrollments.length > 0) {
          const items: InvoiceLineItem[] = [];
          let total = 0;
          enrollments.forEach((en) => {
            en.plans.forEach((plan) => {
              const price = getGelanggangClassPrice(en.gelanggang, plan, gelanggangList, isAdult);
              items.push({
                id: `item-yuran-${en.gelanggang}-${plan}`,
                description: `Yuran Bulanan ${plan} (${en.gelanggang})`,
                quantity: 1,
                unitPrice: price,
                amount: price
              });
              total += price;
            });
          });

          if (items.length > 0) {
            setLineItems(items);
            setInvAmount(total);
            setInvTitle(`Yuran Bulanan - ${targetMember.name}`);
            setInvGelanggang(targetMember.gelanggang || 'Wadi AnNour');
          }
        } else {
          // Default fallback based on member's age & gelanggang
          const gName = targetMember.gelanggang || 'Wadi AnNour';
          const defaultPrice = getGelanggangClassPrice(gName, 'Silat', gelanggangList, isAdult);
          const defaultPlan = !isAdult && (gName === 'Wadi AnNour' || gName === 'Pusaka Warisan') ? 'Silat Kanak-kanak' : 'Silat';
          setLineItems([
            { id: 'item-1', description: `Yuran Bulanan ${defaultPlan} (${gName})`, quantity: 1, unitPrice: defaultPrice, amount: defaultPrice }
          ]);
          setInvAmount(defaultPrice);
          setInvTitle(`Yuran Bulanan - ${targetMember.name}`);
          setInvGelanggang(gName);
        }
      }
    }
  }, [selectedMemberId, invCategory, payerType, membersList, gelanggangList, editingInvoice]);

  // Strict Role-based Invoice Scoping:
  // - Executive Management (Pengerusi, Timbalan Pengerusi, Setiausaha, Bendahari): See all invoices persatuan.
  // - Ketua Gelanggang: See invoices for assigned Gelanggang or generated for themselves.
  // - Waris: See their own invoices and their linked children's invoices.
  // - Regular Members (Ahli role): ONLY see invoices belonging to themselves.
  const scopedInvoices: InvoiceItem[] = invoices.filter((inv: InvoiceItem) => {
    if (isExecutive) return true;
    if (isKetuaGelanggang) {
      const uG = (currentUser?.gelanggang || userGelanggang || '').toLowerCase();
      const invG = (inv.gelanggang || '').toLowerCase();
      const bankK = getInvoiceBankAccountKey(inv);
      const matchesGelanggang =
        (uG.includes('pusaka') && (invG.includes('pusaka') || bankK === 'pusaka_warisan')) ||
        (uG.includes('wadi') && (invG.includes('wadi') || bankK === 'wadi_annour')) ||
        invG === uG;

      return (
        matchesGelanggang ||
        inv.memberId === currentUser?.id ||
        (currentUser?.name && inv.memberName.trim().toLowerCase() === currentUser.name.trim().toLowerCase())
      );
    }
    if (!currentUser) return false;

    if (canUserAddChild(currentUser)) {
      const childrenIds = membersList.filter(m => m.parentId === currentUser.id).map(m => m.id);
      return (
        inv.memberId === currentUser.id ||
        childrenIds.includes(inv.memberId) ||
        (currentUser.phone && (inv.memberPhone === currentUser.phone || inv.vendorPhone === currentUser.phone)) ||
        (currentUser.name && inv.memberName.trim().toLowerCase() === currentUser.name.trim().toLowerCase())
      );
    }

    return (
      inv.memberId === currentUser.id ||
      (currentUser.phone && (inv.memberPhone === currentUser.phone || inv.vendorPhone === currentUser.phone)) ||
      (currentUser.name && inv.memberName.trim().toLowerCase() === currentUser.name.trim().toLowerCase())
    );
  });

  const pusakaDetail = gelanggangList.find(g => g.name.toLowerCase().includes('pusaka'));
  const wadiDetail = gelanggangList.find(g => g.name.toLowerCase().includes('wadi'));

  const bankAccounts = useMemo(() => [
    {
      key: 'all' as PaymentBankAccountKey,
      label: 'Semua Invois (Konsolidasi)',
      shortLabel: 'Semua Akaun',
      bankName: 'Semua Bank (Konsolidasi)',
      icon: Wallet
    },
    {
      key: 'persatuan' as PaymentBankAccountKey,
      label: 'Akaun Induk Persatuan (Bank Islam)',
      shortLabel: 'Persatuan',
      bankName: persatuanSettings.bankName || 'Bank Islam',
      accountNo: persatuanSettings.accountNo || '02011010087654',
      icon: Landmark
    },
    {
      key: 'pusaka_warisan' as PaymentBankAccountKey,
      label: 'Gelanggang Pusaka Warisan (Maybank)',
      shortLabel: 'Pusaka Warisan',
      bankName: pusakaDetail?.qrBankName || 'Maybank',
      accountNo: pusakaDetail?.qrAccountNo || '152068654321',
      icon: Building2
    },
    {
      key: 'wadi_annour' as PaymentBankAccountKey,
      label: 'Gelanggang Wadi AnNour (Bank Islam)',
      shortLabel: 'Wadi AnNour',
      bankName: wadiDetail?.qrBankName || 'Bank Islam',
      accountNo: wadiDetail?.qrAccountNo || '02093010041234',
      icon: Building2
    }
  ], [persatuanSettings, pusakaDetail, wadiDetail]);

  // Filter by bank account / entity for management
  const accountFilteredInvoices: InvoiceItem[] = useMemo(() => {
    if (!canGenerateInvoice || selectedAccountKey === 'all') return scopedInvoices;
    return scopedInvoices.filter((inv: InvoiceItem) => getInvoiceBankAccountKey(inv) === selectedAccountKey);
  }, [scopedInvoices, canGenerateInvoice, selectedAccountKey]);

  // Per-account stats map for summary cards and selectors
  const accountStatsMap = useMemo(() => {
    const map: Record<PaymentBankAccountKey, { count: number; unpaidCount: number; unpaidAmount: number }> = {
      all: { count: scopedInvoices.length, unpaidCount: 0, unpaidAmount: 0 },
      persatuan: { count: 0, unpaidCount: 0, unpaidAmount: 0 },
      pusaka_warisan: { count: 0, unpaidCount: 0, unpaidAmount: 0 },
      wadi_annour: { count: 0, unpaidCount: 0, unpaidAmount: 0 },
    };

    scopedInvoices.forEach((inv: InvoiceItem) => {
      const k = getInvoiceBankAccountKey(inv);
      const isUnpaid = inv.status === 'Belum Dibayar' || inv.status === 'Menunggu Pengesahan';
      const amt = Number(inv.amount) || 0;

      if (map[k]) {
        map[k].count++;
        if (isUnpaid) {
          map[k].unpaidCount++;
          map[k].unpaidAmount += amt;
        }
      }

      if (isUnpaid) {
        map.all.unpaidCount++;
        map.all.unpaidAmount += amt;
      }
    });

    return map;
  }, [scopedInvoices]);

  // Filter by status ('all' | 'unpaid' | 'pending' | 'paid')
  const statusFilteredInvoices: InvoiceItem[] = useMemo(() => {
    return accountFilteredInvoices.filter((inv: InvoiceItem) => {
      if (filterStatus === 'unpaid') return inv.status === 'Belum Dibayar';
      if (filterStatus === 'pending') return inv.status === 'Menunggu Pengesahan';
      if (filterStatus === 'paid') return inv.status === 'Lulus / Dibayar';
      return true;
    });
  }, [accountFilteredInvoices, filterStatus]);

  // Filter by search query
  const searchFilteredInvoices: InvoiceItem[] = useMemo(() => {
    if (!publicSearchQuery.trim()) return statusFilteredInvoices;
    const q = publicSearchQuery.toLowerCase().trim();
    return statusFilteredInvoices.filter((inv: InvoiceItem) => {
      return (
        inv.invoiceNo.toLowerCase().includes(q) ||
        inv.memberName.toLowerCase().includes(q) ||
        inv.title.toLowerCase().includes(q) ||
        (inv.vendorPhone && inv.vendorPhone.includes(q)) ||
        (inv.memberPhone && inv.memberPhone.includes(q))
      );
    });
  }, [statusFilteredInvoices, publicSearchQuery]);

  const sortOptions: { id: InvoiceSortOption; label: string }[] = [
    { id: 'unpaid-first', label: 'Belum Dibayar Dahulu' },
    { id: 'date-desc', label: 'Tarikh: Terkini → Terdahulu' },
    { id: 'date-asc', label: 'Tarikh: Terdahulu → Terkini' },
    { id: 'amount-desc', label: 'Jumlah: Tertinggi (RM)' },
    { id: 'amount-asc', label: 'Jumlah: Terendah (RM)' },
    { id: 'name-asc', label: 'Nama Pembayar: A → Z' },
    { id: 'name-desc', label: 'Nama Pembayar: Z → A' },
    { id: 'invoice-no', label: 'No. Invois' },
  ];

  // User-controlled sorting for management & members
  const sortedInvoices: InvoiceItem[] = useMemo(() => {
    const list = [...searchFilteredInvoices];
    return list.sort((a: InvoiceItem, b: InvoiceItem) => {
      switch (sortBy) {
        case 'unpaid-first': {
          const isAUnpaid = a.status === 'Belum Dibayar' || a.status === 'Menunggu Pengesahan';
          const isBUnpaid = b.status === 'Belum Dibayar' || b.status === 'Menunggu Pengesahan';
          if (isAUnpaid && !isBUnpaid) return -1;
          if (!isAUnpaid && isBUnpaid) return 1;
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }
        case 'date-desc':
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        case 'date-asc':
          return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
        case 'amount-desc':
          return (Number(b.amount) || 0) - (Number(a.amount) || 0);
        case 'amount-asc':
          return (Number(a.amount) || 0) - (Number(b.amount) || 0);
        case 'name-asc':
          return (a.memberName || '').localeCompare(b.memberName || '');
        case 'name-desc':
          return (b.memberName || '').localeCompare(a.memberName || '');
        case 'invoice-no':
          return b.invoiceNo.localeCompare(a.invoiceNo);
        default:
          return 0;
      }
    });
  }, [searchFilteredInvoices, sortBy]);

  const unpaidCount = accountFilteredInvoices.filter((i: InvoiceItem) => i.status === 'Belum Dibayar').length;
  const pendingCount = accountFilteredInvoices.filter((i: InvoiceItem) => i.status === 'Menunggu Pengesahan').length;
  const paidCount = accountFilteredInvoices.filter((i: InvoiceItem) => i.status === 'Lulus / Dibayar').length;
  const totalUnpaidAmount = accountFilteredInvoices.filter((i: InvoiceItem) => i.status === 'Belum Dibayar' || i.status === 'Menunggu Pengesahan').reduce((s: number, i: InvoiceItem) => s + (Number(i.amount) || 0), 0);

  // Handle Execute Sahkan Bayaran (Bendahari / Ketua Gelanggang / Persatuan)
  const handleExecuteSahkanBayaran = async (inv: InvoiceItem) => {
    if (!inv.receiptUrl || inv.receiptUrl.trim() === '') {
      showToast('error', 'Tiada Bukti Resit', 'Invois belum dimuat naik resit bayaran. Resit diperlukan sebelum pengesahan.');
      return;
    }
    setIsSubmittingSahkan(true);
    try {
      const now = new Date();
      const paidDate = now.toISOString().split('T')[0];
      const paidTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
      const paidAtCombined = `${paidDate} ${paidTime}`;

      const roleTitle = currentUser?.role || 'Pengurusan';
      const confirmerName = currentUser?.name || 'Pegawai Pengesah';
      const bankKey = getInvoiceBankAccountKey(inv);
      const paymentChannel = bankKey === 'persatuan'
        ? `Disahkan Manual (Persatuan - ${roleTitle}: ${confirmerName})`
        : `Disahkan Manual (Gelanggang ${inv.gelanggang} - ${roleTitle}: ${confirmerName})`;

      const updatedInvoice: InvoiceItem = {
        ...inv,
        status: 'Lulus / Dibayar',
        paidAt: paidAtCombined,
        paymentMethod: inv.paymentMethod || paymentChannel
      };

      const newInvoices = await saveInvoiceDB(updatedInvoice);
      setInvoices(newInvoices);

      let cat: FinancialTransaction['category'] = 'Yuran Bulanan';
      if (inv.category === 'Yuran Pendaftaran') cat = 'Yuran Pendaftaran';
      else if (inv.category === 'Yuran Bengkung') cat = 'Yuran Bengkung';
      else if (inv.category === 'Peralatan / Uniform') cat = 'Pembelian Alatan';
      else if (inv.category === 'Sumbangan') cat = 'Sumbangan';
      else if (inv.category) cat = inv.category as any;

      const matchedTx: FinancialTransaction = {
        id: `tx-inv-${inv.id}-${Date.now()}`,
        date: paidDate,
        category: cat,
        description: `Bayaran Invois ${inv.invoiceNo}: ${inv.title} (${paymentChannel})`,
        amount: Number(inv.amount) || 0,
        type: 'Income',
        payerOrPayee: inv.memberName || 'Ahli Persatuan',
        gelanggang: (inv.gelanggang === 'Semua Gelanggang' ? 'ASMM' : inv.gelanggang) || 'ASMM',
        recordedBy: `${confirmerName} (${roleTitle})`,
        status: 'Disahkan',
        toyibBillCode: inv.toyibBillCode || inv.invoiceNo,
        receiptUrl: inv.receiptUrl || ''
      };
      await saveTransactionDB(matchedTx);

      window.dispatchEvent(new CustomEvent('asmm_db_updated'));

      showToast(
        'success',
        'Bayaran Berjaya Disahkan!',
        `Invois ${inv.invoiceNo} (RM ${inv.amount}) telah disahkan lulus oleh ${confirmerName}.`
      );

      setConfirmingInvoice(null);

      if (singleInvoiceView && singleInvoiceView.id === inv.id) {
        setSingleInvoiceView(updatedInvoice);
      }
    } catch {
      showToast('error', 'Ralat Mengesahkan Bayaran', 'Gagal menyimpan status pengesahan ke pangkalan data.');
    } finally {
      setIsSubmittingSahkan(false);
    }
  };

  // Handle URL change when clicking account or facet tabs
  const handleSelectAccountKey = (key: PaymentBankAccountKey) => {
    setSelectedAccountKey(key);
    setSelectedInvoiceIds(new Set());
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (key === 'all') {
        url.searchParams.delete('account');
        url.searchParams.delete('bank');
      } else {
        url.searchParams.set('account', key);
      }
      if (singleInvoiceView) {
        setSingleInvoiceView(null);
        url.searchParams.delete('inv');
        url.searchParams.delete('invoice');
      }
      window.history.pushState({ status: filterStatus, account: key }, '', url.toString());
    }
  };

  const handleSelectFilterStatus = (newStatus: 'all' | 'unpaid' | 'pending' | 'paid') => {
    setFilterStatus(newStatus);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (newStatus === 'all') {
        url.searchParams.delete('status');
        url.searchParams.delete('tab');
      } else {
        url.searchParams.set('status', newStatus);
      }
      if (singleInvoiceView) {
        setSingleInvoiceView(null);
        url.searchParams.delete('inv');
        url.searchParams.delete('invoice');
      }
      window.history.pushState({ status: newStatus, account: selectedAccountKey }, '', url.toString());
    }
  };

  const handleResetFilters = () => {
    setPublicSearchQuery('');
    setFilterStatus('all');
    setSelectedAccountKey('all');
    setSelectedInvoiceIds(new Set());
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.delete('status');
      url.searchParams.delete('tab');
      url.searchParams.delete('account');
      url.searchParams.delete('bank');
      window.history.pushState({ status: 'all', account: 'all' }, '', url.toString());
    }
  };

  // Handle Pay Invoice via ToyyibPay FPX (Generates bill & opens ToyyibPay checkout WITHOUT auto-confirming)
  const handlePayInvoice = async (inv: InvoiceItem) => {
    setIsProcessingId(inv.id);

    try {
      const res = await createToyyibPayBill({
        categoryCode: TOYYIBPAY_CONFIG.categoryCode,
        billName: `Invois ${inv.invoiceNo}`,
        billDescription: `${inv.title} - Persatuan Seni Silat Awang Sulung Merah Muda`,
        billPriceSetting: 1,
        billPayorName: currentUser?.name || inv.memberName,
        billPayorEmail: currentUser?.email || 'ahli@asmm.org.my',
        billPayorPhone: currentUser?.phone || inv.memberPhone || '0124475157',
        billAmount: inv.amount,
      });

      // Update status to 'Menunggu Pengesahan' with toyibBillCode (Do NOT auto-confirm locally!)
      const updatedInvoice: InvoiceItem = {
        ...inv,
        status: 'Menunggu Pengesahan',
        toyibBillCode: res.billCode
      };
      const newInvoices = await saveInvoiceDB(updatedInvoice);
      setInvoices(newInvoices);

      // Open ToyyibPay payment gateway URL in same tab
      if (res.paymentUrl) {
        window.location.href = res.paymentUrl;
      }
    } catch {
      showToast('error', 'Gagal menjana transaksi ToyyibPay FPX');
    } finally {
      setIsProcessingId(null);
    }
  };

  // Handle Re-Verifying Payment Status with ToyyibPay API
  const handleVerifyInvoiceStatus = async (inv: InvoiceItem) => {
    if (!inv.toyibBillCode) return;
    setIsVerifyingId(inv.id);
    showToast('info', 'Menyemak status ToyyibPay API...', `Bill Code: ${inv.toyibBillCode}`);

    try {
      const res = await verifyToyyibPayBillStatus(inv.toyibBillCode);

      if (res.status === 'Disahkan') {
        const paidDate = res.paymentDate ? res.paymentDate.split('T')[0] : new Date().toISOString().split('T')[0];

        const updatedInvoice: InvoiceItem = {
          ...inv,
          status: 'Lulus / Dibayar',
          paidAt: paidDate
        };
        const newInvoices = await saveInvoiceDB(updatedInvoice);
        setInvoices(newInvoices);

        // Record income transaction in Turso DB ONLY after verification succeeds!
        await saveTransactionDB({
          id: `ASMM-TX-${Date.now()}`,
          date: paidDate,
          category: inv.category,
          description: `Bayaran FPX Invois ${inv.invoiceNo}: ${inv.title} (${inv.toyibBillCode})`,
          amount: inv.amount,
          type: 'Income',
          payerOrPayee: inv.memberName,
          gelanggang: inv.gelanggang,
          recordedBy: currentUser?.name || 'Sistem FPX API',
          status: 'Disahkan',
          toyibBillCode: inv.toyibBillCode
        });

        showToast('success', 'Bayaran Disahkan Lulus oleh ToyyibPay!', `Invois ${inv.invoiceNo} telah dijelaskan.`);

        setSingleInvoiceView(updatedInvoice);
      } else {
        showToast('warning', 'Transaksi Belum Dikesan Lulus', res.message);
      }
    } catch {
      showToast('error', 'Gagal menyemak status dengan ToyyibPay API');
    } finally {
      setIsVerifyingId(null);
    }
  };

  // Handle Generate Custom Invoice (Executive, Ketua Gelanggang & Vendor Invoicing)
  const handleGenerateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invTitle.trim() || invAmount <= 0) {
      showToast('error', 'Sila isi tajuk invois dan jumlah bayaran');
      return;
    }

    let mName = '';
    let mIc = '';
    let mPhone = '';
    let mEmail = '';
    let mAddress = '';
    let mId = '';

    if (payerType === 'Vendor / Pihak Ketiga') {
      if (!vendorName.trim()) {
        showToast('error', 'Sila isi nama vendor / syarikat / pembayar pihak ketiga');
        return;
      }
      mName = vendorName.trim();
      mPhone = vendorPhone.trim();
      mEmail = vendorEmail.trim();
      mAddress = vendorAddress.trim();
      mId = `VENDOR-${Date.now()}`;
    } else {
      const targetMember = membersList.find(m => m.id === selectedMemberId);
      mName = targetMember ? targetMember.name : (currentUser?.name || 'Pesilat ASMM');
      mIc = targetMember ? targetMember.icNo : (currentUser?.icNo || '');
      mPhone = targetMember ? targetMember.phone : (currentUser?.phone || '');
      mEmail = targetMember ? targetMember.email : (currentUser?.email || '');
      mAddress = targetMember ? (targetMember.address || `Gelanggang ${targetMember.gelanggang}, Kedah`) : (currentUser?.address || 'Gelanggang Wadi AnNour, Kulim, Kedah');
      mId = selectedMemberId || currentUser?.id || 'asmm-001';
    }

    // Ensure lineItems amounts are clean
    const validItems = lineItems.map(item => ({
      ...item,
      quantity: Number(item.quantity) || 1,
      unitPrice: Number(item.unitPrice) || 0,
      amount: (Number(item.quantity) || 1) * (Number(item.unitPrice) || 0)
    }));

    const computedTotal = validItems.length > 0
      ? validItems.reduce((sum, i) => sum + i.amount, 0)
      : invAmount;

    const finalStatus = editingInvoice ? invStatus : 'Belum Dibayar';
    const finalReceiptUrl = invReceiptUrl ? resolveCloudinaryUrl(invReceiptUrl) : (editingInvoice?.receiptUrl ? resolveCloudinaryUrl(editingInvoice.receiptUrl) : undefined);

    const newInv: InvoiceItem = {
      id: editingInvoice ? editingInvoice.id : `inv-${Date.now()}`,
      invoiceNo: editingInvoice ? editingInvoice.invoiceNo : `INV-2026-${Math.floor(100 + Math.random() * 900)}`,
      memberId: mId,
      memberName: mName,
      memberIcNo: mIc,
      memberPhone: mPhone,
      address: mAddress,
      payerType: payerType,
      vendorPhone: mPhone,
      vendorEmail: mEmail,
      vendorAddress: mAddress,
      gelanggang: invGelanggang,
      category: invCategory as any,
      title: invTitle.trim(),
      description: invDesc.trim(),
      items: validItems,
      amount: computedTotal > 0 ? computedTotal : invAmount,
      dueDate: invDueDate,
      createdAt: editingInvoice ? editingInvoice.createdAt : new Date().toISOString().split('T')[0],
      status: finalStatus,
      paidAt: finalStatus === 'Lulus / Dibayar' ? (editingInvoice?.paidAt || new Date().toISOString().replace('T', ' ').substring(0, 19)) : undefined,
      receiptUrl: finalReceiptUrl,
      toyibBillCode: editingInvoice ? editingInvoice.toyibBillCode : undefined,
      billingCycle: supportsBillingCycle(invCategory) ? billingCycle : undefined,
      billingMonths: supportsBillingCycle(invCategory) ? (billingCycle === 'Tahunan' ? 12 : 1) : undefined,
      originalMonthlyPrice: supportsBillingCycle(invCategory) && billingCycle === 'Tahunan' && validItems.length === 1 ? validItems[0].unitPrice : undefined,
      discountPercent: supportsBillingCycle(invCategory) && billingCycle === 'Tahunan' ? YEARLY_DISCOUNT_PERCENT : undefined
    };

    const updated = await saveInvoiceDB(newInv);
    setInvoices(updated);

    // If marked paid, automatically synchronize to transactions audit ledger with receipt
    if (newInv.status === 'Lulus / Dibayar') {
      const txDate = newInv.paidAt
        ? newInv.paidAt.split(' ')[0]
        : (newInv.createdAt ? newInv.createdAt.split(' ')[0] : new Date().toISOString().split('T')[0]);

      let cat: FinancialTransaction['category'] = 'Yuran Bulanan';
      if (newInv.category === 'Yuran Pendaftaran') cat = 'Yuran Pendaftaran';
      else if (newInv.category === 'Yuran Bengkung') cat = 'Yuran Bengkung';
      else if (newInv.category === 'Peralatan / Uniform') cat = 'Pembelian Alatan';
      else if (newInv.category === 'Sumbangan') cat = 'Sumbangan';
      else if (newInv.category) cat = newInv.category as any;

      const confirmerName = currentUser?.name || 'Mohammad Hafiz Bin Halim';
      const matchedTx: FinancialTransaction = {
        id: `tx-inv-${newInv.id}`,
        date: txDate,
        category: cat,
        description: `Bayaran Invois ${newInv.invoiceNo}: ${newInv.title} (Disahkan oleh ${confirmerName})`,
        amount: Number(newInv.amount) || 0,
        type: 'Income',
        payerOrPayee: newInv.memberName || 'Ahli Persatuan',
        gelanggang: (newInv.gelanggang === 'Semua Gelanggang' ? 'ASMM' : newInv.gelanggang) || 'ASMM',
        recordedBy: confirmerName,
        status: 'Disahkan',
        toyibBillCode: newInv.toyibBillCode || newInv.invoiceNo,
        receiptUrl: finalReceiptUrl || ''
      };
      await saveTransactionDB(matchedTx);
    }

    showToast('success', editingInvoice ? 'Invois Dikemaskini!' : `Invois ${payerType === 'Vendor / Pihak Ketiga' ? 'Vendor' : 'Ahli'} Dijana!`, `No. Invois: ${newInv.invoiceNo} (RM ${newInv.amount})`);
    setIsCreateModalOpen(false);
    setEditingInvoice(null);

    // Reset Form
    setVendorName('');
    setVendorPhone('');
    setVendorEmail('');
    setVendorAddress('');
    setInvTitle(defaultInvoiceTitle);
    setInvDesc('');
    setInvAmount(40);
    setBillingCycle('Bulanan');
    setInvStatus('Belum Dibayar');
    setInvReceiptUrl('');
    setLineItems([{ id: 'item-1', description: defaultInvoiceTitle, quantity: 1, unitPrice: 40, amount: 40 }]);
  };

  const handleDeleteInvoice = async (id: string, invNo: string) => {
    const updated = await deleteInvoiceDB(id);
    setInvoices(updated);
    setSelectedInvoiceIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    showToast('info', 'Invois Dipadam', `Invois ${invNo} telah dipadam.`);
  };

  const handleToggleSelectAll = (selectableIds: string[]) => {
    if (selectedInvoiceIds.size === selectableIds.length && selectableIds.length > 0) {
      setSelectedInvoiceIds(new Set());
    } else {
      setSelectedInvoiceIds(new Set(selectableIds));
    }
  };

  const handleToggleSelect = (id: string) => {
    setSelectedInvoiceIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleBatchDeleteSelected = async () => {
    if (selectedInvoiceIds.size === 0) return;
    const count = selectedInvoiceIds.size;
    const confirmDelete = window.confirm(`Adakah anda pasti ingin memadam ${count} invois terpilih?`);
    if (!confirmDelete) return;

    let updated = [...invoices];
    for (const id of Array.from(selectedInvoiceIds)) {
      updated = await deleteInvoiceDB(id);
    }
    setInvoices(updated);
    setSelectedInvoiceIds(new Set());
    showToast('info', 'Invois Dipadam', `${count} invois terpilih telah dipadam.`);
  };

  const handleShareInvoice = (inv: InvoiceItem) => {
    const payUrl = `${window.location.origin}/payments?inv=${inv.invoiceNo}`;
    const targetPhone = inv.vendorPhone || inv.memberPhone;
    if (targetPhone) {
      const phone = targetPhone.replace(/[^0-9]/g, '');
      const formattedPhone = phone.startsWith('6') ? phone : `6${phone}`;
      const msg = encodeURIComponent(
        `Assalamualaikum / Salam Sejahtera ${inv.memberName},\n\n` +
        `Berikut adalah pautan invois rasmi PSSASMM Kedah:\n` +
        `• No. Invois: ${inv.invoiceNo}\n` +
        `• Perkara: ${inv.title}\n` +
        `• Jumlah: RM ${inv.amount}.00\n\n` +
        `Anda boleh menyemak dokumen dan membuat pembayaran di pautan berikut:\n` +
        `${payUrl}\n\n` +
        `Terima kasih.\n*Persatuan Seni Silat Awang Sulung Merah Muda Kedah*`
      );
      window.open(`https://wa.me/${formattedPhone}?text=${msg}`, '_blank');
    } else {
      navigator.clipboard.writeText(payUrl);
      showToast('success', 'Pautan Invois Disalin!', payUrl);
    }
  };

  const handleViewReceipt = (inv: InvoiceItem) => {
    saveViewScroll('invoices_list');
    setReceiptData(null);
    setPrintInvoiceData(null);
    setSingleInvoiceView(inv);
    setActiveQrInvoice(null);
    if (typeof window !== 'undefined' && window.history && window.history.pushState) {
      const search = new URLSearchParams(window.location.search);
      if (search.get('inv') !== inv.invoiceNo) {
        const newUrl = `${window.location.pathname}?inv=${encodeURIComponent(inv.invoiceNo)}`;
        window.history.pushState({ inv: inv.invoiceNo }, '', newUrl);
      }
    }
  };

  const handleOpenQrPayment = (inv: InvoiceItem) => {
    saveViewScroll('invoices_list');
    setSingleInvoiceView(null);
    setActiveQrInvoice(inv);
    if (typeof window !== 'undefined' && window.history && window.history.pushState) {
      const search = new URLSearchParams(window.location.search);
      if (search.get('inv') !== inv.invoiceNo || search.get('pay') !== 'qr') {
        const newUrl = `${window.location.pathname}?inv=${encodeURIComponent(inv.invoiceNo)}&pay=qr`;
        window.history.pushState({ inv: inv.invoiceNo, pay: 'qr' }, '', newUrl);
      }
    }
  };

  const handleBackToInvoices = () => {
    if (typeof window !== 'undefined' && window.history) {
      const search = new URLSearchParams(window.location.search);
      if (search.has('inv') || search.has('invoice')) {
        if (window.history.state?.inv) {
          window.history.back();
          return;
        } else {
          const cleanUrl = window.location.pathname;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      }
    }
    setSingleInvoiceView(null);
    setActiveQrInvoice(null);
    restoreViewScroll('invoices_list');
  };

  if (activeQrInvoice) {
    const matchedGelanggang = gelanggangList.find(g => g.name === activeQrInvoice.gelanggang);
    return (
      <QrPaymentPageView
        invoice={activeQrInvoice}
        gelanggang={matchedGelanggang}
        currentUser={currentUser}
        onBack={handleBackToInvoices}
        onSuccess={(updatedInvoice) => {
          setInvoices(prev => prev.map(inv => inv.id === updatedInvoice.id ? updatedInvoice : inv));
          handleBackToInvoices();
          showToast('success', 'Bayaran QR Disahkan!', `Resit bayaran untuk Invois ${updatedInvoice.invoiceNo} telah direkodkan.`);
        }}
      />
    );
  }

  if (singleInvoiceView) {
    const isPaid = singleInvoiceView.status === 'Lulus / Dibayar';
    const singleGelanggang = gelanggangList.find(g => g.name === singleInvoiceView.gelanggang);
    const isSingleQr = !persatuanSettings.fpxEnabled || singleGelanggang?.paymentPreference === 'qr_ketua' || (!singleGelanggang && singleInvoiceView.gelanggang === 'Pusaka Warisan');

    const targetMember = membersList.find(m => m.id === singleInvoiceView.memberId || m.name === singleInvoiceView.memberName);
    const memberBadgeId = sanitizeMemberId(singleInvoiceView.memberId || targetMember?.id || '');
    const memberAddress = singleInvoiceView.vendorAddress || singleInvoiceView.address || targetMember?.address || `Gelanggang ${singleInvoiceView.gelanggang}, Kedah Darul Aman`;
    const memberPhone = singleInvoiceView.vendorPhone || singleInvoiceView.memberPhone || targetMember?.phone || '-';
    const memberEmail = singleInvoiceView.vendorEmail || targetMember?.email || '';
    const invoiceReceiptDate = singleInvoiceView.createdAt || singleInvoiceView.paidAt?.split(' ')[0] || singleInvoiceView.dueDate || '2026-10-01';

    return (
      <div className="w-full max-w-4xl mx-auto space-y-6 pb-12">
        {/* Top Navigation & Action Header */}
        <div className="bg-slate-900 text-white p-4 rounded-3xl shadow-lg border border-slate-800 flex items-center justify-between gap-4 flex-wrap no-print">
          <div className="flex items-center gap-3">
            <div>
              <div className="text-xs font-black text-amber-400 uppercase tracking-wider">
                {isPaid ? 'Resit Rasmi PSSASMM' : 'Invois Rasmi PSSASMM'}
              </div>
              <div className="text-sm font-extrabold text-white font-mono">{singleInvoiceView.invoiceNo}</div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {!isPaid && isSingleQr && (
              <button
                onClick={() => handleOpenQrPayment(singleInvoiceView)}
                className="px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-black text-xs rounded-2xl transition-all shadow-md flex items-center gap-2 cursor-pointer"
              >
                <QrCode className="w-4 h-4" />
                <span>Bayar Kod QR</span>
              </button>
            )}

            {!isPaid && !isSingleQr && (
              <button
                onClick={() => handlePayInvoice(singleInvoiceView)}
                disabled={isProcessingId === singleInvoiceView.id}
                className="px-5 py-2.5 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs rounded-2xl transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <CreditCard className="w-4 h-4" />
                <span>{isProcessingId === singleInvoiceView.id ? 'Menjana FPX...' : 'Bayar FPX Sekarang'}</span>
              </button>
            )}

            {!isPaid && canSahkanBayaran(singleInvoiceView) && (
              <button
                onClick={() => setConfirmingInvoice(singleInvoiceView)}
                className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs rounded-2xl transition-all shadow-md flex items-center gap-2 cursor-pointer"
                title="Sahkan Penerimaan Bayaran Invois"
              >
                <CheckCircle2 className="w-4 h-4 text-emerald-100" />
                <span>Sahkan Bayaran</span>
              </button>
            )}

            {singleInvoiceView.receiptUrl && (
              <button
                onClick={() => setViewingReceiptUrl(singleInvoiceView.receiptUrl || null)}
                className="px-4 py-2.5 bg-emerald-700 hover:bg-emerald-800 text-white font-extrabold text-xs rounded-2xl transition-all shadow-md flex items-center gap-2 cursor-pointer border border-emerald-600"
                title="Lihat Bukti Resit / Slip Bayaran"
              >
                <Receipt className="w-4 h-4 text-emerald-200" />
                <span>Lihat Resit</span>
              </button>
            )}

            <button
              onClick={() => {
                const link = `${window.location.origin}/payments?inv=${singleInvoiceView.invoiceNo}`;
                navigator.clipboard.writeText(link);
                showToast('success', 'Pautan Disalin!', `Pautan terus dokumen sedia dikongsi.`);
              }}
              className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs rounded-2xl transition-all flex items-center gap-1.5 cursor-pointer border border-slate-700"
            >
              <Share2 className="w-3.5 h-3.5 text-amber-400" />
              <span>Salin Pautan</span>
            </button>

            <button
              onClick={() => window.print()}
              className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs rounded-2xl transition-all flex items-center gap-1.5 cursor-pointer shadow-sm"
            >
              <Printer className="w-3.5 h-3.5" />
              <span>Cetak / PDF</span>
            </button>

            {currentUser && (
              <button
                onClick={handleBackToInvoices}
                className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs rounded-2xl transition-all flex items-center gap-1.5 cursor-pointer border border-slate-700"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                <span>Semua Invois</span>
              </button>
            )}
          </div>
        </div>

        {/* Dedicated Single Invoice Card / Document Box */}
        <div className="printable-invoice bg-white p-6 sm:p-10 rounded-3xl border border-slate-200 shadow-xl space-y-5 sm:space-y-6 relative overflow-hidden">
          {/* Top Right Corner Diagonal Ribbon — flush with container edge */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-30">
            <div
              className={`absolute top-8 -right-16 w-[320px] py-2.5 text-center font-black text-[11px] uppercase tracking-widest rotate-45 shadow-md border-y border-white/30 ${
                isPaid ? 'bg-emerald-600 text-white' : 'bg-red-700 text-white'
              }`}
              style={{
                backgroundColor: isPaid ? '#059669' : '#B91C1C',
                color: '#ffffff',
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact'
              }}
            >
              {isPaid ? 'DIBAYAR / PAID' : 'BELUM DIBAYAR'}
            </div>
          </div>

          {/* Header Format Surat Rasmi */}
          <div className="relative pb-4 border-b-4 border-double border-slate-900 pt-2 pr-28">
            <div className="flex items-center gap-4">
              <img src="/logo-pssasmm.png" alt="Logo PSSASMM" className="w-16 h-16 sm:w-20 sm:h-20 object-contain shrink-0" />
              <div className="space-y-1 text-left min-w-0">
                <h1 className="text-sm sm:text-base font-black tracking-tight text-slate-900 uppercase leading-tight">
                  PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA KEDAH
                </h1>
                <div className="text-xs font-extrabold text-asmm-red uppercase tracking-wider font-mono">
                  PENDAFTARAN ROS: PPM-007-02-28082022
                </div>
                <p className="text-xs text-slate-600 leading-tight">
                  263, Persiaran Utama 1/9, Kulim Utama, 09000 Kulim, Kedah Darul Aman
                </p>
                <p className="text-xs text-slate-500 font-mono">
                  Tel: 012-4475157 • Emel: rasmi@asmm.org.my • Portal: asmm.pages.dev
                </p>
              </div>
            </div>
          </div>

          {/* Title Header */}
          <div className="text-center space-y-1 py-1 sm:py-2">
            <h2 className="text-base sm:text-xl font-black text-slate-900 uppercase tracking-wide">
              {isPaid ? 'RESIT RASMI PEMBAYARAN PSSASMM' : 'INVOIS RASMI PSSASMM'}
            </h2>
            <p className="text-xs font-mono text-slate-500">
              No. Rujukan: {singleInvoiceView.invoiceNo} • Tarikh: {invoiceReceiptDate}
            </p>
          </div>

          {/* Recipient Info Block */}
          <div className="text-xs border border-slate-300 p-3.5 sm:p-4 rounded-2xl bg-slate-50 space-y-1">
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
              {singleInvoiceView.payerType === 'Vendor / Pihak Ketiga' || singleInvoiceView.memberId?.startsWith('VENDOR-')
                ? 'PEMBAYAR / DITERBITKAN KEPADA:'
                : 'AHLI / DITERBITKAN KEPADA:'}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-extrabold text-slate-900 text-sm">{singleInvoiceView.memberName}</span>
              {memberBadgeId && memberBadgeId !== 'N/A' && (
                <span className="font-mono text-[10px] font-bold bg-slate-200/80 text-slate-700 px-1.5 py-0.5 rounded border border-slate-300">
                  {memberBadgeId}
                </span>
              )}
            </div>
            <div className="text-slate-700 font-medium">{memberAddress}</div>
            <div className="font-mono text-slate-700">Tel / WhatsApp: {memberPhone}</div>
            {memberEmail && (
              <div className="text-slate-600 font-mono">Emel: {memberEmail}</div>
            )}
          </div>

          {/* Particulars & Itemized Table */}
          <div className="space-y-4 overflow-x-auto">
            {/* Summary Particulars Table */}
            <table className="w-full text-xs border-collapse border border-slate-300">
              <tbody>
                <tr className="border-b border-slate-300 bg-slate-50">
                  <td className="p-2 border border-slate-300 font-bold text-slate-600 w-1/3">No. Invois:</td>
                  <td className="p-2 border border-slate-300 font-mono font-bold text-slate-900">{singleInvoiceView.invoiceNo}</td>
                </tr>
                <tr className="border-b border-slate-300">
                  <td className="p-2 border border-slate-300 font-bold text-slate-600">Gelanggang Sah:</td>
                  <td className="p-2 border border-slate-300 font-bold text-asmm-red">{singleInvoiceView.gelanggang}</td>
                </tr>
                <tr className="border-b border-slate-300 bg-slate-50">
                  <td className="p-2 border border-slate-300 font-bold text-slate-600">Kategori Invois:</td>
                  <td className="p-2 border border-slate-300 font-bold text-slate-900">{singleInvoiceView.category}</td>
                </tr>
                <tr className="border-b border-slate-300">
                  <td className="p-2 border border-slate-300 font-bold text-slate-600">Status FPX:</td>
                  <td className={`p-2 border border-slate-300 font-black ${isPaid ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {singleInvoiceView.status}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Line Items Detail Table */}
            <table className="w-full text-xs border-collapse border border-slate-300">
              <thead>
                <tr className="bg-slate-900 text-white font-extrabold text-[11px]">
                  <th className="p-2 border border-slate-700 text-center w-10">#</th>
                  <th className="p-2 border border-slate-700 text-left">Perihal Item / Perkhidmatan</th>
                  <th className="p-2 border border-slate-700 text-center w-20">Kuantiti</th>
                  <th className="p-2 border border-slate-700 text-right w-28">Harga (RM)</th>
                  <th className="p-2 border border-slate-700 text-right w-28">Jumlah (RM)</th>
                </tr>
              </thead>
              <tbody>
                {(singleInvoiceView.items && singleInvoiceView.items.length > 0 ? singleInvoiceView.items : [
                  { id: '1', description: singleInvoiceView.title, quantity: 1, unitPrice: singleInvoiceView.amount, amount: singleInvoiceView.amount }
                ]).map((it, idx) => (
                  <tr key={it.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                    <td className="p-2 border border-slate-300 text-center font-mono">{idx + 1}</td>
                    <td className="p-2 border border-slate-300 font-bold text-slate-900">{it.description}</td>
                    <td className="p-2 border border-slate-300 text-center font-mono font-bold">{it.quantity}</td>
                    <td className="p-2 border border-slate-300 text-right font-mono">RM {Number(it.unitPrice).toFixed(2)}</td>
                    <td className="p-2 border border-slate-300 text-right font-mono font-bold text-slate-900">RM {Number(it.amount).toFixed(2)}</td>
                  </tr>
                ))}
                <tr className="bg-slate-100 font-extrabold text-xs border-t-2 border-slate-900">
                  <td colSpan={4} className="p-2 text-slate-900 text-right border border-slate-300 uppercase tracking-wider font-bold">
                    JUMLAH INVOIS (RM):
                  </td>
                  <td className="p-2 font-mono text-slate-900 text-right border border-slate-300 font-black">
                    RM {Number(singleInvoiceView.amount).toFixed(2)}
                  </td>
                </tr>
                <tr className="bg-emerald-50 font-extrabold text-xs">
                  <td colSpan={4} className="p-2 text-emerald-900 text-right border border-slate-300 uppercase tracking-wider font-bold">
                    JUMLAH DIBAYAR (RM):
                  </td>
                  <td className="p-2 font-mono text-emerald-800 text-right border border-slate-300 font-black">
                    RM {isPaid ? Number(singleInvoiceView.amount).toFixed(2) : '0.00'}
                  </td>
                </tr>
                <tr className="bg-slate-50 font-bold text-xs">
                  <td colSpan={4} className="p-2 text-slate-900 text-right border border-slate-300 uppercase tracking-wider">
                    BAKI TERTUNGGAK (RM):
                  </td>
                  <td className="p-2 font-mono text-emerald-700 text-right border border-slate-300">
                    {isPaid ? 'RM 0.00' : `RM ${Number(singleInvoiceView.amount).toFixed(2)}`}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* FPX Audit Record Table (Positioned BELOW Itemized Breakdown if paid) */}
            {isPaid && (
              <div className="mt-4 pt-2">
                <div className="text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1">
                  REKOD AUDIT TRANSAKSI FPX:
                </div>
                <table className="w-full text-xs border-collapse border border-slate-300">
                  <tbody>
                    <tr className="border-b border-slate-300 bg-slate-50">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600 w-1/3">Jenis / Saluran Transaksi:</td>
                      <td className="p-2 border border-slate-300 font-bold text-slate-900">
                        {singleInvoiceView.paymentMethod || 'Disahkan Pentadbiran / Superadmin'}
                      </td>
                    </tr>
                    <tr className="border-b border-slate-300">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600">Kod Transaksi FPX / Bill Code:</td>
                      <td className="p-2 border border-slate-300 font-mono font-extrabold text-emerald-800">
                        {singleInvoiceView.toyibBillCode || 'ASMM-FPX-LOCAL'}
                      </td>
                    </tr>
                    <tr className="border-b border-slate-300 bg-slate-50">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600">Tarikh & Masa Pembayaran:</td>
                      <td className="p-2 border border-slate-300 font-mono font-bold text-slate-900">
                        {singleInvoiceView.paidAt || `${singleInvoiceView.createdAt} 10:54:22`}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}

            {/* Attached Payment Receipt / Slip Card (No Print) */}
            {singleInvoiceView.receiptUrl && (
              <div className="no-print mt-4 p-4 bg-emerald-50/80 border border-emerald-200 rounded-2xl flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center font-bold shrink-0 border border-emerald-300">
                    <Receipt className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div>
                    <div className="text-xs font-black text-emerald-950">Bukti Slip / Resit Bayaran Dilampirkan</div>
                    <div className="text-[10px] text-emerald-700 font-medium">Dokumen telah dimuat naik dan sedia disemak / disahkan</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setViewingReceiptUrl(singleInvoiceView.receiptUrl || null)}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl flex items-center gap-1.5 transition shadow-xs cursor-pointer"
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span>Papar Bukti Resit</span>
                </button>
              </div>
            )}
          </div>

          <div className="pt-6 text-[10px] text-slate-400 text-center border-t border-slate-200">
            Ini adalah cetakan dokumen komputer rasmi. Tiada tanda tangan fizikal diperlukan. (ROS: PPM-007-02-28082022)
          </div>
        </div>

        {/* SINGLE BILL PDF PRINT CONTAINER (Strictly Single Document Output Mounted to document.body) */}
        {typeof document !== 'undefined' && createPortal(
          <div className="print-document-portal hidden print:block font-sans text-slate-900 relative w-full bg-white">
            {/* 45-Degree Slanted Corner Ribbon — flush with page edge */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none z-50 print-ribbon-clip">
              <div
                className="absolute top-10 -right-24 w-[380px] py-2.5 transform rotate-45 text-center text-[11px] font-black tracking-widest text-white uppercase shadow-md flex items-center justify-center border-y border-white/30"
                style={{
                  backgroundColor: isPaid ? '#059669' : '#B91C1C',
                  color: '#ffffff',
                  WebkitPrintColorAdjust: 'exact',
                  printColorAdjust: 'exact'
                }}
              >
                <span>{isPaid ? 'DIBAYAR / PAID' : 'BELUM DIBAYAR'}</span>
              </div>
            </div>

            <div className="space-y-6 max-w-3xl mx-auto p-8 pt-6">
              {/* Header Format Surat Rasmi */}
              <div className="relative pb-4 border-b-4 border-double border-slate-900 pt-2 pr-28">
                <div className="flex items-center gap-4">
                  <img src="/logo-pssasmm.png" alt="Logo PSSASMM" className="w-20 h-20 object-contain shrink-0" />
                  <div className="space-y-1 text-left min-w-0">
                    <h1 className="text-base font-black tracking-tight text-slate-900 uppercase leading-tight">
                      PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA KEDAH
                    </h1>
                    <div className="text-xs font-extrabold text-asmm-red uppercase tracking-wider font-mono">
                      PENDAFTARAN ROS: PPM-007-02-28082022
                    </div>
                    <p className="text-xs text-slate-600 leading-tight">
                      263, Persiaran Utama 1/9, Kulim Utama, 09000 Kulim, Kedah Darul Aman
                    </p>
                    <p className="text-xs text-slate-500 font-mono">
                      Tel: 012-4475157 • Emel: rasmi@asmm.org.my • Portal: asmm.pages.dev
                    </p>
                  </div>
                </div>
              </div>

              {/* Title Header */}
              <div className="text-center space-y-1 py-2">
                <h2 className="text-lg font-black text-slate-900 uppercase tracking-wide">
                  {isPaid ? 'RESIT RASMI PEMBAYARAN PSSASMM' : 'INVOIS RASMI PSSASMM'}
                </h2>
                <p className="text-xs font-mono text-slate-500">
                  No. Rujukan: {singleInvoiceView.invoiceNo} • Tarikh: {singleInvoiceView.createdAt || singleInvoiceView.dueDate}
                </p>
              </div>

              {/* Recipient Info Block */}
              <div className="text-xs border border-slate-300 p-3.5 rounded-xl bg-slate-50 mb-3 space-y-1">
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                  {singleInvoiceView.payerType === 'Vendor / Pihak Ketiga' || singleInvoiceView.memberId?.startsWith('VENDOR-')
                    ? 'PEMBAYAR / DITERBITKAN KEPADA:'
                    : 'AHLI / DITERBITKAN KEPADA:'}
                </div>
                <div className="font-extrabold text-slate-900 text-sm">{singleInvoiceView.memberName}</div>
                <div className="text-slate-700 font-medium">
                  {singleInvoiceView.vendorAddress || singleInvoiceView.address || `Gelanggang ${singleInvoiceView.gelanggang}, Kedah Darul Aman`}
                </div>
                <div className="font-mono text-slate-700">
                  Tel / WhatsApp: {singleInvoiceView.vendorPhone || singleInvoiceView.memberPhone || '-'}
                </div>
                {singleInvoiceView.vendorEmail && (
                  <div className="text-slate-600 font-mono">Emel: {singleInvoiceView.vendorEmail}</div>
                )}
              </div>

              {/* Particulars & Itemized Table */}
              <div className="space-y-3">
                <table className="w-full text-xs border-collapse border border-slate-300">
                  <tbody>
                    <tr className="border-b border-slate-300 bg-slate-50">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600 w-1/3">No. Invois:</td>
                      <td className="p-2 border border-slate-300 font-mono font-bold text-slate-900">{singleInvoiceView.invoiceNo}</td>
                    </tr>
                    <tr className="border-b border-slate-300">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600">Gelanggang Sah:</td>
                      <td className="p-2 border border-slate-300 font-bold text-asmm-red">{singleInvoiceView.gelanggang}</td>
                    </tr>
                    <tr className="border-b border-slate-300 bg-slate-50">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600">Kategori Invois:</td>
                      <td className="p-2 border border-slate-300 font-bold text-slate-900">{singleInvoiceView.category}</td>
                    </tr>
                    <tr className="border-b border-slate-300">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600">Status FPX:</td>
                      <td className={`p-2 border border-slate-300 font-black ${isPaid ? 'text-emerald-700' : 'text-asmm-red'}`}>
                        {singleInvoiceView.status}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Line Items Detail Table */}
                <table className="w-full text-xs border-collapse border border-slate-300">
                  <thead>
                    <tr className="bg-slate-900 text-white font-extrabold text-[11px]">
                      <th className="p-2 border border-slate-700 text-center w-10">#</th>
                      <th className="p-2 border border-slate-700 text-left">Perihal Item / Perkhidmatan</th>
                      <th className="p-2 border border-slate-700 text-center w-16">Kuantiti</th>
                      <th className="p-2 border border-slate-700 text-right w-24">Harga (RM)</th>
                      <th className="p-2 border border-slate-700 text-right w-28">Jumlah (RM)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(singleInvoiceView.items && singleInvoiceView.items.length > 0 ? singleInvoiceView.items : [
                      { id: '1', description: singleInvoiceView.title, quantity: 1, unitPrice: singleInvoiceView.amount, amount: singleInvoiceView.amount }
                    ]).map((it, idx) => (
                      <tr key={it.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="p-2 border border-slate-300 text-center font-mono">{idx + 1}</td>
                        <td className="p-2 border border-slate-300 font-bold text-slate-900">{it.description}</td>
                        <td className="p-2 border border-slate-300 text-center font-mono font-bold">{it.quantity}</td>
                        <td className="p-2 border border-slate-300 text-right font-mono">RM {Number(it.unitPrice).toFixed(2)}</td>
                        <td className="p-2 border border-slate-300 text-right font-mono font-bold text-slate-900">RM {Number(it.amount).toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-100 font-extrabold text-xs border-t-2 border-slate-900">
                      <td colSpan={4} className="p-2 text-slate-900 text-right border border-slate-300 uppercase tracking-wider">
                        JUMLAH INVOIS (RM):
                      </td>
                      <td className="p-2 font-mono text-slate-900 text-right border border-slate-300">
                        RM {Number(singleInvoiceView.amount).toFixed(2)}
                      </td>
                    </tr>
                    <tr className={`font-extrabold text-xs ${isPaid ? 'bg-emerald-50 text-emerald-900' : 'bg-slate-50 text-slate-700'}`}>
                      <td colSpan={4} className="p-2 text-right border border-slate-300 uppercase tracking-wider">
                        JUMLAH DIBAYAR (RM):
                      </td>
                      <td className="p-2 font-mono text-right border border-slate-300 font-black">
                        RM {isPaid ? Number(singleInvoiceView.amount).toFixed(2) : '0.00'}
                      </td>
                    </tr>
                    <tr className={`font-black text-xs ${isPaid ? 'bg-slate-200/80 text-slate-900' : 'bg-red-50 text-asmm-red'}`}>
                      <td colSpan={4} className="p-2 text-right border border-slate-300 uppercase tracking-wider">
                        BAKI TERTUNGGAK (RM):
                      </td>
                      <td className="p-2 font-mono text-right border border-slate-300 text-xs">
                        {isPaid ? 'RM 0.00' : `RM ${Number(singleInvoiceView.amount).toFixed(2)}`}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* FPX Audit Record Table (Positioned BELOW Itemized Breakdown if paid) */}
                {isPaid && (
                  <div className="mt-4 pt-2">
                    <div className="text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1">
                      REKOD AUDIT TRANSAKSI FPX:
                    </div>
                    <table className="w-full text-xs border-collapse border border-slate-300">
                      <tbody>
                        <tr className="border-b border-slate-300 bg-slate-50">
                          <td className="p-2 border border-slate-300 font-bold text-slate-600 w-1/3">Jenis / Saluran Transaksi:</td>
                          <td className="p-2 border border-slate-300 font-bold text-slate-900">{singleInvoiceView.paymentMethod || 'ToyyibPay FPX Online Banking'}</td>
                        </tr>
                        <tr className="border-b border-slate-300">
                          <td className="p-2 border border-slate-300 font-bold text-slate-600">Kod Transaksi FPX / Bill Code:</td>
                          <td className="p-2 border border-slate-300 font-mono font-extrabold text-emerald-800">{singleInvoiceView.toyibBillCode || 'ASMM-FPX-LOCAL'}</td>
                        </tr>
                        <tr className="border-b border-slate-300 bg-slate-50">
                          <td className="p-2 border border-slate-300 font-bold text-slate-600">Tarikh & Masa Pembayaran:</td>
                          <td className="p-2 border border-slate-300 font-mono font-bold text-slate-900">
                            {singleInvoiceView.paidAt || singleInvoiceView.createdAt}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="pt-6 text-[10px] text-slate-400 text-center border-t border-slate-200">
                Ini adalah cetakan dokumen komputer rasmi. Tiada tanda tangan fizikal diperlukan. (ROS: PPM-007-02-28082022)
              </div>
            </div>
          </div>,
          document.body
        )}
        {/* MODAL PAPARAN BUKTI RESIT / SLIP BAYARAN */}
        <ReceiptViewerModal
          isOpen={Boolean(viewingReceiptUrl)}
          onClose={() => setViewingReceiptUrl(null)}
          receiptUrl={viewingReceiptUrl}
          title="Paparan Bukti Resit Bayaran"
          invoiceNo={singleInvoiceView.invoiceNo}
        />

        {/* MODAL PENGESAHAN PENERIMAAN BAYARAN */}
        {confirmingInvoice && (
          <Modal
            isOpen={Boolean(confirmingInvoice)}
            onClose={() => setConfirmingInvoice(null)}
            title="Sahkan Penerimaan Bayaran Invois"
            maxWidth="max-w-md"
          >
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2 text-xs">
                <div className="flex justify-between items-center py-1 border-b border-slate-200">
                  <span className="text-slate-500 font-semibold">No. Invois:</span>
                  <span className="font-mono font-extrabold text-slate-900">{confirmingInvoice.invoiceNo}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-slate-200">
                  <span className="text-slate-500 font-semibold">Pembayar:</span>
                  <span className="font-bold text-slate-900">{confirmingInvoice.memberName}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-slate-200">
                  <span className="text-slate-500 font-semibold">Tujuan / Butiran:</span>
                  <span className="font-medium text-slate-800 text-right max-w-[240px] truncate">{confirmingInvoice.title}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-slate-200">
                  <span className="text-slate-500 font-semibold">Gelanggang Sah:</span>
                  <span className="font-bold text-slate-800">{confirmingInvoice.gelanggang}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-slate-200">
                  <span className="text-slate-500 font-semibold">Kaedah / Saluran Bayaran:</span>
                  <span className="font-bold text-blue-700">
                    {getInvoiceBankAccountKey(confirmingInvoice) === 'persatuan'
                      ? 'Akaun Induk Persatuan'
                      : `Akaun Gelanggang (${confirmingInvoice.gelanggang})`}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-slate-200">
                  <span className="text-slate-500 font-semibold">Pegawai Mengesahkan:</span>
                  <span className="font-bold text-slate-900">
                    {currentUser?.name} ({currentUser?.role})
                  </span>
                </div>
                <div className="flex justify-between items-center pt-1.5">
                  <span className="text-slate-700 font-extrabold text-sm">Jumlah Bayaran Disahkan:</span>
                  <span className="text-lg font-black font-mono text-emerald-600">
                    RM {confirmingInvoice.amount}.00
                  </span>
                </div>
              </div>

              {confirmingInvoice.receiptUrl && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between gap-2 text-xs">
                  <span className="text-blue-900 font-medium truncate">Bukti Slip / Resit Disediakan Pembayar</span>
                  <button
                    type="button"
                    onClick={() => setViewingReceiptUrl(confirmingInvoice.receiptUrl || null)}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs flex items-center gap-1 shrink-0 cursor-pointer"
                  >
                    <Eye className="w-3 h-3" />
                    <span>Lihat Slip</span>
                  </button>
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  disabled={isSubmittingSahkan}
                  onClick={() => setConfirmingInvoice(null)}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all cursor-pointer disabled:opacity-50"
                >
                  Batal
                </button>
                <button
                  type="button"
                  disabled={isSubmittingSahkan}
                  onClick={() => handleExecuteSahkanBayaran(confirmingInvoice)}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {isSubmittingSahkan ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Mengesahkan...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 text-emerald-200" />
                      <span>Sahkan Penerimaan Bayaran</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </Modal>
        )}
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 min-h-[75vh]">
      {/* Public Vendor & Guest Header Banner */}
      {!currentUser && (
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 text-white p-6 rounded-3xl shadow-lg border border-slate-700 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="space-y-1">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-400/20 text-amber-300 font-extrabold text-[11px] rounded-full border border-amber-400/30">
                <ShieldCheck className="w-3.5 h-3.5 text-amber-400" />
                Portal Pembayaran Awam & Vendor
              </span>
              <h2 className="text-xl font-black text-white">Semakan & Bayaran Invois FPX</h2>
              <p className="text-xs text-slate-300 max-w-xl">
                Tanpa perlu log masuk. Sila masukkan No. Invois Rasmi atau No. Telefon terdaftar untuk menyemak dan membuat pembayaran terus melalui ToyyibPay FPX.
              </p>
            </div>

            <div className="w-full sm:w-80">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={publicSearchQuery}
                  onChange={(e) => setPublicSearchQuery(e.target.value)}
                  placeholder="Cari No. Invois (INV-2026-xxx) / No. Tel..."
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 rounded-2xl text-xs text-white placeholder-slate-400 focus:outline-none focus:border-amber-400 transition-all"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Authenticated Only KPI & Filter Bar */}
      {currentUser && (
        <>
          {/* Fixed Header Banner */}
          <PageHeader
            badgeText="FPX Online Gateway"
            badgeIcon={CreditCard}
            title="Senarai Invois & Yuran"
          >
            {canGenerateInvoice && (
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="px-4 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-2xl transition-all shadow-sm flex items-center justify-center gap-2 cursor-pointer"
              >
                <Plus className="w-4 h-4" />
                <span>Jana Invois</span>
              </button>
            )}
          </PageHeader>

          {/* Bank Account / Entiti Filter Bar for Management */}
          {canGenerateInvoice && (
            <div className="space-y-2.5 no-print">
              <div className="flex items-center justify-between">
                <div className="text-xs font-black uppercase tracking-wider text-slate-800 flex items-center gap-1.5">
                  <Landmark className="w-3.5 h-3.5 text-asmm-red" />
                  <span>Pilihan Akaun Bank / Entiti Invois</span>
                </div>
                <span className="text-[11px] text-slate-500 font-medium">
                  {selectedAccountKey === 'all'
                    ? 'Paparan Konsolidasi (Semua Invois)'
                    : `Akaun: ${bankAccounts.find((a: any) => a.key === selectedAccountKey)?.shortLabel}`}
                </span>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 sm:gap-3">
                {bankAccounts.map((acc: any) => {
                  const isSelected = selectedAccountKey === acc.key;
                  const stats = accountStatsMap[acc.key as PaymentBankAccountKey] || { count: 0, unpaidCount: 0, unpaidAmount: 0 };
                  const IconComp = acc.icon;

                  return (
                    <button
                      key={acc.key}
                      type="button"
                      onClick={() => {
                        handleSelectAccountKey(acc.key);
                      }}
                      className={`p-3 sm:p-3.5 rounded-2xl border text-left transition-all cursor-pointer relative flex flex-col justify-between ${
                        isSelected
                          ? 'bg-slate-900 text-white border-slate-900 shadow-md ring-2 ring-slate-900/10'
                          : 'bg-white hover:bg-slate-50 text-slate-800 border-slate-200 shadow-2xs'
                      }`}
                    >
                      <div>
                        <div className="flex items-center justify-between gap-1.5 mb-1.5">
                          <div className={`p-1.5 rounded-xl ${isSelected ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-700'}`}>
                            <IconComp className="w-4 h-4" />
                          </div>
                          <span
                            className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full ${
                              isSelected ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {stats.count} invois
                          </span>
                        </div>
                        <div className={`text-xs font-black truncate leading-tight ${isSelected ? 'text-white' : 'text-slate-900'}`}>
                          {acc.shortLabel}
                        </div>
                        <div className={`text-[10px] font-medium truncate mt-0.5 ${isSelected ? 'text-slate-300' : 'text-slate-500'}`}>
                          {acc.bankName.replace(' Malaysia Berhad', '')}
                        </div>
                      </div>

                      <div className={`mt-2.5 pt-2 border-t flex items-center justify-between ${isSelected ? 'border-white/15' : 'border-slate-100'}`}>
                        <span className={`text-[10px] font-bold ${isSelected ? 'text-slate-300' : 'text-slate-400'}`}>
                          Tertunggak
                        </span>
                        <span
                          className={`text-xs font-black font-mono ${
                            isSelected
                              ? stats.unpaidAmount > 0 ? 'text-amber-300' : 'text-emerald-300'
                              : stats.unpaidAmount > 0 ? 'text-rose-600' : 'text-emerald-700'
                          }`}
                        >
                          RM {stats.unpaidAmount.toLocaleString('ms-MY')}.00
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Summary KPI Banner */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Invois Belum Dibayar</span>
                <div className="text-xl font-extrabold text-amber-600 font-mono mt-0.5">{unpaidCount} Invois</div>
              </div>
              <div className="p-3 bg-amber-50 rounded-2xl text-amber-600 border border-amber-100">
                <AlertCircle className="w-5 h-5" />
              </div>
            </div>

            <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Jumlah Tertunggak</span>
                <div className="text-xl font-extrabold text-rose-600 font-mono mt-0.5">RM {totalUnpaidAmount.toLocaleString('ms-MY')}.00</div>
              </div>
              <div className="p-3 bg-rose-50 rounded-2xl text-rose-600 border border-rose-100">
                <DollarSign className="w-5 h-5" />
              </div>
            </div>

            <div className="bg-white p-5 rounded-3xl border border-slate-200 shadow-sm flex items-center justify-between">
              <div>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Akaun Aktif</span>
                <div className="text-sm font-extrabold text-emerald-700 font-mono mt-0.5 truncate">
                  {selectedAccountKey === 'all'
                    ? 'Semua Akaun (Konsolidasi)'
                    : bankAccounts.find((a: any) => a.key === selectedAccountKey)?.shortLabel}
                </div>
              </div>
              <div className="p-3 bg-emerald-50 rounded-2xl text-emerald-600 border border-emerald-100">
                <ShieldCheck className="w-5 h-5" />
              </div>
            </div>
          </div>

          {/* Redesigned Responsive Filter & Search Control Panel */}
          <div className="bg-white p-3.5 sm:p-5 rounded-3xl border border-slate-200/90 shadow-xs space-y-3.5 no-print">
            {/* Top Tier: Responsive Status Segmented Tabs */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-1.5 p-1 bg-slate-100/80 rounded-2xl overflow-x-auto max-w-full no-scrollbar">
                {[
                  { id: 'all', label: 'Semua', count: accountFilteredInvoices.length, color: 'slate' },
                  { id: 'unpaid', label: 'Belum Dibayar', count: unpaidCount, color: 'amber' },
                  { id: 'pending', label: 'Menunggu Pengesahan', count: pendingCount, color: 'blue' },
                  { id: 'paid', label: 'Telah Dibayar', count: paidCount, color: 'emerald' }
                ].map((t) => {
                  const isActive = filterStatus === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => handleSelectFilterStatus(t.id as any)}
                      className={`px-3 py-1.5 sm:px-3.5 sm:py-2 rounded-xl text-xs font-extrabold whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 sm:gap-2 shrink-0 ${
                        isActive
                          ? 'bg-slate-900 text-white shadow-xs'
                          : 'text-slate-600 hover:text-slate-900 hover:bg-white/80'
                      }`}
                    >
                      <span>{t.label}</span>
                      <span
                        className={`text-[10px] font-mono font-bold px-1.5 sm:px-2 py-0.5 rounded-full transition-colors ${
                          isActive
                            ? 'bg-white/20 text-white'
                            : t.color === 'amber'
                              ? 'bg-amber-100 text-amber-800'
                              : t.color === 'blue'
                                ? 'bg-blue-100 text-blue-800'
                                : t.color === 'emerald'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : 'bg-slate-200/70 text-slate-700'
                        }`}
                      >
                        {t.count}
                      </span>
                    </button>
                  );
                })}
              </div>

              {(publicSearchQuery || filterStatus !== 'all') && (
                <button
                  onClick={handleResetFilters}
                  className="text-[11px] font-bold text-slate-500 hover:text-red-700 transition-colors flex items-center gap-1 cursor-pointer ml-auto"
                >
                  <RotateCcw className="w-3 h-3" />
                  <span>Set Semula</span>
                </button>
              )}
            </div>

            {/* Bottom Tier: Quick Search & Sort By Dropdown (Full width responsive) */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3 pt-3 border-t border-slate-100">
              <div className="relative flex-1 w-full">
                <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={publicSearchQuery}
                  onChange={(e) => setPublicSearchQuery(e.target.value)}
                  placeholder="Cari no. invois, nama pembayar, atau perincian..."
                  className="w-full pl-10 pr-9 py-2 sm:py-2.5 bg-slate-50 hover:bg-slate-100/50 focus:bg-white border border-slate-200 focus:border-slate-800 rounded-2xl text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/10 transition-all shadow-3xs"
                />
                {publicSearchQuery && (
                  <button
                    onClick={() => setPublicSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 cursor-pointer"
                    title="Kosongkan carian"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Sort By Dropdown */}
              <div className="relative flex items-center shrink-0 w-full sm:w-auto">
                <ArrowUpDown className="w-3.5 h-3.5 text-slate-500 absolute left-3.5 pointer-events-none" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as InvoiceSortOption)}
                  className="w-full sm:w-auto pl-9 pr-8 py-2 sm:py-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 focus:border-slate-800 rounded-2xl text-xs font-bold text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900/10 cursor-pointer shadow-3xs appearance-none transition-all"
                  title="Susun Senarai Invois"
                >
                  {sortOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="absolute right-3 pointer-events-none text-slate-400 text-[10px]">▼</span>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Invoice Ledger Cards List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CardSkeleton />
            <CardSkeleton />
          </div>
        ) : !currentUser && !publicSearchQuery.trim() ? (
          <div className="bg-white p-12 rounded-3xl border border-slate-200 text-center space-y-4 shadow-sm">
            <div className="p-4 bg-slate-50 w-16 h-16 rounded-full mx-auto flex items-center justify-center text-asmm-red border border-slate-200">
              <ShieldCheck className="w-8 h-8 text-asmm-red" />
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-extrabold text-slate-900">Portal Semakan & Bayaran Invois Awam</h3>
              <p className="text-xs text-slate-500 max-w-md mx-auto leading-relaxed">
                Atas faktor keselamatan dan privasi, senarai invois pihak lain tidak dipaparkan secara terbuka. Sila masukkan <strong>No. Invois Rasmi</strong> (contoh: <code className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-800">INV-2026-641</code>) atau <strong>No. Telefon</strong> terdaftar anda pada borang carian di atas untuk menyemak dan membuat pembayaran.
              </p>
            </div>
          </div>
        ) : sortedInvoices.length === 0 ? (
          <div className="bg-white p-12 rounded-3xl border border-slate-200 text-center space-y-3">
            <Receipt className="w-12 h-12 text-slate-300 mx-auto" />
            <h4 className="text-sm font-extrabold text-slate-800">Tiada Invois Dijumpai</h4>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              Sila pastikan No. Invois atau No. Telefon yang dimasukkan adalah tepat.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {/* Batch Select Bar */}
            <div className={`p-3 sm:px-4 sm:py-2.5 rounded-2xl border transition-all flex items-center justify-between gap-3 ${
              selectedInvoiceIds.size > 0
                ? 'bg-amber-50/70 border-amber-300 shadow-xs text-amber-950'
                : 'bg-white border-slate-200/90 shadow-3xs text-slate-700'
            }`}>
              <label className="flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={sortedInvoices.length > 0 && selectedInvoiceIds.size === sortedInvoices.length}
                  onChange={() => handleToggleSelectAll(sortedInvoices.map((i: InvoiceItem) => i.id))}
                  className="w-4 h-4 rounded border-slate-300 text-amber-600 focus:ring-amber-500 cursor-pointer"
                />
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs sm:text-sm font-bold text-slate-800">
                    Pilih Semua Invois
                  </span>
                  <span className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded-full ${
                    selectedInvoiceIds.size > 0 ? 'bg-amber-200/80 text-amber-900' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {selectedInvoiceIds.size} / {sortedInvoices.length} dipilih
                  </span>
                </div>
              </label>

              {selectedInvoiceIds.size > 0 && canGenerateInvoice && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSelectedInvoiceIds(new Set())}
                    className="text-xs font-bold text-slate-600 hover:text-slate-900 px-2.5 py-1 rounded-xl transition-colors cursor-pointer"
                  >
                    Batal
                  </button>
                  <button
                    onClick={handleBatchDeleteSelected}
                    className="text-xs font-extrabold text-white bg-rose-600 hover:bg-rose-700 px-3.5 py-1.5 rounded-xl shadow-xs transition-all cursor-pointer flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-white" />
                    <span>Hapus Terpilih ({selectedInvoiceIds.size})</span>
                  </button>
                </div>
              )}
            </div>

            {/* Invoices List */}
            {sortedInvoices.map((inv: InvoiceItem) => {
              const isUnpaid = inv.status === 'Belum Dibayar';
              const isPending = inv.status === 'Menunggu Pengesahan';
              const isPaid = inv.status === 'Lulus / Dibayar';
              const isSelected = selectedInvoiceIds.has(inv.id);

              const targetGelanggang = gelanggangList.find(g => g.name === inv.gelanggang);
              const isQrPayment = !persatuanSettings.fpxEnabled || targetGelanggang?.paymentPreference === 'qr_ketua' || (!targetGelanggang && inv.gelanggang === 'Pusaka Warisan');

              const memberObj = membersList.find(m => m.id === inv.memberId || m.name === inv.memberName);
              const rawMemberId = inv.memberId || memberObj?.id || '';
              const displayMemberId = sanitizeMemberId(rawMemberId);

              return (
                <div
                  key={inv.id}
                  className={`bg-white p-5 sm:p-6 rounded-3xl border transition-all duration-300 shadow-xs ${
                    isUnpaid
                      ? 'border-amber-300 hover:border-amber-400 shadow-amber-500/[0.03]'
                      : isPending
                        ? 'border-blue-300 hover:border-blue-400 shadow-blue-500/[0.03]'
                        : 'border-slate-200 hover:border-slate-300 shadow-slate-500/[0.02]'
                  }`}
                >
                  {/* Top Row: Checkbox, Badge No, Category & Status Pill */}
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2.5 flex-wrap">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(inv.id)}
                        className="w-4 h-4 rounded border-slate-300 text-amber-500 focus:ring-amber-400 cursor-pointer"
                      />
                      <button
                        onClick={() => handleViewReceipt(inv)}
                        className="text-[11px] font-mono font-bold text-slate-800 bg-slate-50 hover:bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-lg transition-colors cursor-pointer"
                        title="Lihat Invois / Cetak Resit"
                      >
                        {inv.invoiceNo}
                      </button>
                      {inv.payerType === 'Vendor / Pihak Ketiga' || inv.memberId?.startsWith('VENDOR-') ? (
                        <span className="text-[10px] font-black px-2.5 py-1 rounded-lg bg-amber-50 text-amber-800 border border-amber-200 uppercase tracking-wider flex items-center gap-1 shrink-0">
                          <Building2 className="w-2.5 h-2.5 text-amber-600" />
                          Vendor / Syarikat
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold text-slate-600 bg-slate-50 border border-slate-200 px-2.5 py-1 rounded-lg uppercase tracking-wider shrink-0">
                          {inv.category}
                        </span>
                      )}
                      {(() => {
                        const bankKey = getInvoiceBankAccountKey(inv);
                        const badgeMap = {
                          persatuan: { label: 'Persatuan', bg: 'bg-red-50 text-red-700 border-red-200/80', dot: 'bg-red-600' },
                          pusaka_warisan: { label: 'Pusaka Warisan', bg: 'bg-amber-50 text-amber-800 border-amber-200/80', dot: 'bg-amber-500' },
                          wadi_annour: { label: 'Wadi AnNour', bg: 'bg-blue-50 text-blue-800 border-blue-200/80', dot: 'bg-blue-600' }
                        };
                        const b = badgeMap[bankKey] || badgeMap.persatuan;
                        return (
                          <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-lg border ${b.bg} shrink-0`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${b.dot}`} />
                            <span>{b.label}</span>
                          </span>
                        );
                      })()}
                    </div>

                    {/* Status Pill with colored bullet */}
                    <div>
                      {isUnpaid && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-800 bg-amber-50/90 border border-amber-200 rounded-full px-3 py-1">
                          <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0"></span>
                          Belum Dibayar
                        </span>
                      )}
                      {isPending && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-800 bg-blue-50/90 border border-blue-200 rounded-full px-3 py-1 animate-pulse">
                          <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0"></span>
                          Menunggu Pengesahan
                        </span>
                      )}
                      {isPaid && (
                        <span className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-800 bg-emerald-50/90 border border-emerald-200 rounded-full px-3 py-1">
                          <span className="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
                          Lulus / Dibayar
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Title & Description */}
                  <div className="mt-3.5 space-y-1">
                    <h4
                      onClick={() => handleViewReceipt(inv)}
                      className="text-base sm:text-lg font-black text-slate-900 leading-snug cursor-pointer hover:text-red-700 transition-colors"
                      title="Lihat Invois / Cetak Resit"
                    >
                      {inv.title}
                    </h4>
                    {inv.description && (
                      <p className="text-xs text-slate-500 leading-relaxed max-w-4xl">
                        {inv.description}
                      </p>
                    )}
                  </div>

                  {/* Meta Row: Pembayar, Member ID chip, Gelanggang */}
                  <div className="flex items-center flex-wrap gap-x-4 gap-y-1.5 text-xs text-slate-500 font-medium mt-3">
                    <span className="flex items-center gap-1.5">
                      <Users className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="text-slate-400">Pembayar:</span>
                      <strong className="text-slate-800 font-bold">{inv.memberName}</strong>
                    </span>
                    {displayMemberId && displayMemberId !== 'N/A' && (
                      <span className="font-mono text-[10px] font-bold bg-slate-100 text-slate-700 px-2 py-0.5 rounded border border-slate-200 shrink-0">
                        {displayMemberId}
                      </span>
                    )}
                    <span className="flex items-center gap-1.5">
                      <Building2 className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="text-slate-400">Gelanggang:</span>
                      <strong className="text-asmm-red font-bold">{inv.gelanggang}</strong>
                    </span>
                    {inv.billingCycle === 'Tahunan' && (
                      <span className="text-emerald-700 font-extrabold bg-emerald-50 border border-emerald-100 rounded-md px-1.5 py-0.5 text-[10px] uppercase tracking-wider shrink-0">
                        Tahunan · Jimat {inv.discountPercent || YEARLY_DISCOUNT_PERCENT}%
                      </span>
                    )}
                    {inv.toyibBillCode && (
                      <span className="font-mono font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded border border-slate-200 text-[10px] shrink-0">
                        Rujukan: {inv.toyibBillCode}
                      </span>
                    )}
                  </div>

                  {/* Bottom Row: Left Container (Dates & Amount) + Right Action Buttons */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-5 pt-2 border-t border-slate-100/80">
                    {/* Left Container */}
                    <div className="bg-slate-50/80 rounded-2xl p-3 sm:px-4 border border-slate-100 flex items-center gap-6 sm:gap-8 self-start sm:self-auto">
                      {isPaid ? (
                        <div>
                          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                            TARIKH BAYARAN
                          </span>
                          <span className="font-mono font-bold text-emerald-600 text-xs sm:text-sm">
                            {inv.paidAt || inv.createdAt || '2026-10-14 10:54:22'}
                          </span>
                        </div>
                      ) : (
                        <div>
                          <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                            TARIKH AKHIR (DUE)
                          </span>
                          <span className="font-mono font-bold text-amber-600 text-xs sm:text-sm">
                            {inv.dueDate}
                          </span>
                        </div>
                      )}

                      <div className="h-7 w-px bg-slate-200/60 hidden sm:block"></div>

                      <div>
                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider block">
                          JUMLAH BAYARAN
                        </span>
                        <div className="text-base sm:text-lg font-black text-slate-900 font-mono">
                          RM {inv.amount}.00
                        </div>
                      </div>
                    </div>

                    {/* Right Actions */}
                    <div className="flex items-center justify-end flex-wrap gap-2">
                      {/* Kongsi */}
                      <button
                        onClick={() => handleShareInvoice(inv)}
                        className="px-3 py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 font-bold text-xs rounded-xl transition-all border border-slate-200 flex items-center gap-1.5 cursor-pointer"
                        title="Kongsi pautan invois"
                      >
                        <Share2 className="w-3.5 h-3.5 text-slate-500" />
                        <span>Kongsi</span>
                      </button>

                      {/* Lihat Bukti Resit (Jika ada resit dimuat naik) */}
                      {inv.receiptUrl && (
                        <button
                          onClick={() => setViewingReceiptUrl(inv.receiptUrl || null)}
                          className="px-3 py-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold text-xs rounded-xl transition-all border border-emerald-300 flex items-center gap-1.5 cursor-pointer"
                          title="Lihat Bukti Resit / Slip Bayaran Yang Dimuat Naik"
                        >
                          <Eye className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Resit</span>
                        </button>
                      )}

                      {/* Cetak Invois (Unpaid/Pending) */}
                      {!isPaid && (
                        <button
                          onClick={() => handleViewReceipt(inv)}
                          className="px-3 py-2 bg-slate-50 hover:bg-slate-100 text-slate-700 font-bold text-xs rounded-xl transition-all border border-slate-200 flex items-center gap-1.5 cursor-pointer"
                          title="Papar & Cetak Salinan Invois"
                        >
                          <Printer className="w-3.5 h-3.5 text-slate-500" />
                          <span>Cetak</span>
                        </button>
                      )}

                      {/* Edit (Admin/Executive) */}
                      {canGenerateInvoice && (
                        <button
                          onClick={() => handleOpenEditModal(inv)}
                          title="Kemaskini Butiran Invois"
                          className="px-3 py-2 bg-amber-50/60 hover:bg-amber-100 text-amber-800 font-bold text-xs rounded-xl transition-all border border-amber-300 flex items-center gap-1.5 cursor-pointer"
                        >
                          <Pencil className="w-3.5 h-3.5 text-amber-600" />
                          <span>Edit</span>
                        </button>
                      )}

                      {/* Hapus (Admin/Executive) */}
                      {canGenerateInvoice && (
                        <button
                          onClick={() => handleDeleteInvoice(inv.id, inv.invoiceNo)}
                          title="Hapus Invois"
                          className="px-3 py-2 bg-rose-50/60 hover:bg-rose-100 text-rose-700 font-bold text-xs rounded-xl transition-all border border-rose-300 flex items-center gap-1.5 cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                          <span>Hapus</span>
                        </button>
                      )}

                      {/* Sahkan Bayaran (Bendahari / Ketua Gelanggang / Persatuan) */}
                      {!isPaid && canSahkanBayaran(inv) && (
                        <button
                          onClick={() => setConfirmingInvoice(inv)}
                          className="px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs rounded-xl transition-all shadow-xs flex items-center gap-1.5 cursor-pointer"
                          title="Sahkan Penerimaan Bayaran Invois"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-100" />
                          <span>Sahkan Bayaran</span>
                        </button>
                      )}

                      {/* Primary Action Button */}
                      {isPaid && (
                        <button
                          onClick={() => handleViewReceipt(inv)}
                          className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white font-extrabold text-xs sm:text-sm rounded-xl transition-all shadow-sm flex items-center gap-2 cursor-pointer"
                          title="Cetak Resit Rasmi"
                        >
                          <Printer className="w-4 h-4 text-emerald-400" />
                          <span>Cetak Resit</span>
                        </button>
                      )}

                      {isUnpaid && isQrPayment && (
                        <button
                          onClick={() => handleOpenQrPayment(inv)}
                          className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-extrabold text-xs sm:text-sm rounded-xl transition-all shadow-sm flex items-center gap-2 cursor-pointer"
                        >
                          <QrCode className="w-4 h-4 text-white" />
                          <span>Bayar Kod QR</span>
                        </button>
                      )}

                      {isUnpaid && !isQrPayment && persatuanSettings.fpxEnabled && (
                        <button
                          onClick={() => handlePayInvoice(inv)}
                          disabled={isProcessingId === inv.id}
                          className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs sm:text-sm rounded-xl transition-all shadow-sm flex items-center gap-2 cursor-pointer disabled:opacity-50"
                        >
                          <CreditCard className="w-4 h-4 text-white" />
                          <span>{isProcessingId === inv.id ? 'Proses...' : 'Bayar FPX'}</span>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* GENERATE INVOICE MODAL (Executive, Ketua Gelanggang & Vendor Invoicing) */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Jana Invois Rasmi / Vendor Baharu"
      >
        <form onSubmit={handleGenerateInvoice} className="space-y-4">
          {/* Payer Type Switcher */}
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1.5">Jenis Pembayar / Sasaran Invois</label>
            <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-2xl border border-slate-200">
              <button
                type="button"
                onClick={() => {
                  setPayerType('Ahli');
                  setInvTitle(defaultInvoiceTitle);
                  setInvCategory('Yuran Bulanan');
                }}
                className={`py-2 px-3 text-xs font-extrabold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer ${payerType === 'Ahli'
                    ? 'bg-white text-slate-900 shadow-sm'
                    : 'text-slate-500 hover:text-slate-800'
                  }`}
              >
                <UserCheck className="w-3.5 h-3.5" />
                <span>Ahli Berdaftar</span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setPayerType('Vendor / Pihak Ketiga');
                  setInvTitle('Bayaran Sewa Tapak Vendor / Perkhidmatan');
                  setInvCategory('Sewa Tapak / Vendor');
                }}
                className={`py-2 px-3 text-xs font-extrabold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer ${payerType === 'Vendor / Pihak Ketiga'
                    ? 'bg-white text-amber-700 shadow-sm ring-1 ring-amber-300'
                    : 'text-slate-500 hover:text-slate-800'
                  }`}
              >
                <Building2 className="w-3.5 h-3.5 text-amber-600" />
                <span>Vendor / Pihak Ketiga</span>
              </button>
            </div>
          </div>

          {payerType === 'Ahli' ? (
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Sasaran Ahli / Pesilat</label>
              <select
                value={selectedMemberId}
                onChange={(e) => setSelectedMemberId(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white text-xs font-medium"
              >
                <option value="">-- Pilih Pesilat Berdaftar --</option>
                {membersList
                  .filter(m => m.status === 'Aktif')
                  .filter(m => {
                    if (invCategory === 'Yuran Bulanan') {
                      return isEligibleForMonthlyFee(m);
                    }
                    return true;
                  })
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name} ({m.gelanggang} - {m.id})
                    </option>
                  ))}
              </select>
            </div>
          ) : (
            <div className="space-y-3 p-3.5 bg-amber-50/60 border border-amber-200 rounded-2xl">
              <div>
                <label className="block text-xs font-bold text-slate-800 mb-1">
                  Nama Vendor / Syarikat / Pembayar <span className="text-rose-600">*</span>
                </label>
                <input
                  type="text"
                  value={vendorName}
                  onChange={(e) => setVendorName(e.target.value)}
                  placeholder="Contoh: Syarikat Katering Merah Muda / Vendor Tapak Khemah"
                  className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-amber-600 text-xs font-bold text-slate-900"
                  required={payerType === 'Vendor / Pihak Ketiga'}
                />
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">No. Telefon / WhatsApp</label>
                  <input
                    type="text"
                    value={vendorPhone}
                    onChange={(e) => setVendorPhone(e.target.value)}
                    placeholder="Contoh: 0194589201"
                    className="w-full px-3 py-2 rounded-xl bg-white border border-slate-200 focus:border-amber-600 text-xs font-mono text-slate-900"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Emel Vendor (Opsional)</label>
                  <input
                    type="email"
                    value={vendorEmail}
                    onChange={(e) => setVendorEmail(e.target.value)}
                    placeholder="vendor@syarikat.com"
                    className="w-full px-3 py-2 rounded-xl bg-white border border-slate-200 focus:border-amber-600 text-xs text-slate-900"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Alamat Syarikat / Premis Vendor</label>
                <input
                  type="text"
                  value={vendorAddress}
                  onChange={(e) => setVendorAddress(e.target.value)}
                  placeholder="Contoh: No. 12, Jalan Merah 3, Taman Utama, 09000 Kulim, Kedah"
                  className="w-full px-3 py-2 rounded-xl bg-white border border-slate-200 focus:border-amber-600 text-xs text-slate-900"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Kategori Invois</label>
              <select
                value={invCategory}
                onChange={(e) => setInvCategory(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white text-xs font-medium"
              >
                {payerType === 'Ahli' ? (
                  <>
                    <option value="Yuran Bulanan">Yuran Bulanan</option>
                    <option value="Yuran Pendaftaran">Yuran Pendaftaran</option>
                    <option value="Yuran Bengkung">Yuran Bengkung</option>
                    <option value="Pakej Khas">Pakej Khas</option>
                    <option value="Acara & Sijil">Acara & Sijil</option>
                    <option value="Sumbangan">Sumbangan Ahli</option>
                    <option value="Lain-lain">Lain-lain</option>
                  </>
                ) : (
                  <>
                    <option value="Sewa Tapak / Vendor">Sewa Tapak / Vendor</option>
                    <option value="Pembekal / Perkhidmatan">Pembekal / Perkhidmatan</option>
                    <option value="Sumbangan Syarikat">Sumbangan Syarikat</option>
                    <option value="Pihak Ketiga / Vendor">Pihak Ketiga / Vendor</option>
                    <option value="Sewa Gelanggang">Sewa Gelanggang / Fasiliti</option>
                    <option value="Lain-lain">Lain-lain</option>
                  </>
                )}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Gelanggang</label>
              <select
                value={invGelanggang}
                onChange={(e) => setInvGelanggang(e.target.value as any)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white text-xs font-medium"
              >
                <option value="Pusaka Warisan">Pusaka Warisan</option>
                <option value="Wadi AnNour">Wadi AnNour</option>
                <option value="Paya Besar">Paya Besar</option>
                <option value="PSSASMM">PSSASMM</option>
              </select>
            </div>
          </div>

          {/* Billing Cycle Toggle (only for recurring fee categories) */}
          {supportsBillingCycle(invCategory) && (
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">Kitaran Pembayaran</label>
              <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-2xl border border-slate-200">
                <button
                  type="button"
                  onClick={() => {
                    setBillingCycle('Bulanan');
                    // Reset line items to monthly price
                    if (lineItems.length === 1) {
                      const monthlyPrice = lineItems[0].unitPrice;
                      setLineItems([{ ...lineItems[0], quantity: 1, amount: monthlyPrice }]);
                      setInvAmount(monthlyPrice);
                    }
                  }}
                  className={`py-2.5 px-3 text-xs font-extrabold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer ${billingCycle === 'Bulanan'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-800'
                    }`}
                >
                  <Clock className="w-3.5 h-3.5" />
                  <span>Bulanan</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setBillingCycle('Tahunan');
                    // Auto-calculate yearly price with 10% discount
                    if (lineItems.length === 1) {
                      const monthlyPrice = lineItems[0].unitPrice;
                      const yearlyTotal = getBillingAmount(monthlyPrice, 'Tahunan');
                      setLineItems([{ ...lineItems[0], quantity: YEARLY_MONTHS, amount: yearlyTotal }]);
                      setInvAmount(yearlyTotal);
                    }
                  }}
                  className={`py-2.5 px-3 text-xs font-extrabold rounded-xl transition-all flex items-center justify-center gap-1.5 cursor-pointer ${billingCycle === 'Tahunan'
                      ? 'bg-white text-emerald-700 shadow-sm ring-1 ring-emerald-300'
                      : 'text-slate-500 hover:text-slate-800'
                    }`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Tahunan (Jimat {YEARLY_DISCOUNT_PERCENT}%)</span>
                </button>
              </div>
              {billingCycle === 'Tahunan' && lineItems.length === 1 && (
                <div className="mt-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center justify-between">
                  <div className="text-[11px] text-emerald-800 font-bold">
                    <span className="text-emerald-600">12 bulan × RM {lineItems[0].unitPrice.toFixed(2)}</span>
                    <span className="mx-1">→</span>
                    <span className="line-through text-slate-400 font-mono">RM {(lineItems[0].unitPrice * 12).toFixed(2)}</span>
                    <span className="mx-1">→</span>
                    <span className="font-extrabold text-emerald-900 font-mono">RM {getBillingAmount(lineItems[0].unitPrice, 'Tahunan').toFixed(2)}</span>
                  </div>
                  <span className="text-[10px] font-extrabold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-lg border border-emerald-300 shrink-0">
                    Jimat RM {getYearlySavings(lineItems[0].unitPrice).toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          )}

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1">Tajuk Invois</label>
            <input
              type="text"
              value={invTitle}
              onChange={(e) => setInvTitle(e.target.value)}
              placeholder={`Contoh: ${defaultInvoiceTitle}`}
              className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white text-xs text-slate-900"
              required
            />
          </div>



          {/* Itemized Line Items Section */}
          <div className="space-y-2.5 pt-3 border-t border-slate-200">
            <div className="flex items-center justify-between">
              <label className="text-xs font-extrabold text-slate-800 flex items-center gap-1.5">
                <ListPlus className="w-4 h-4 text-asmm-red" />
                <span>Perincian Item & Senarai Harga (Itemized Line Items)</span>
              </label>
              <button
                type="button"
                onClick={handleAddLineItem}
                className="px-2.5 py-1 bg-slate-900 hover:bg-slate-800 text-amber-400 font-extrabold text-[11px] rounded-xl flex items-center gap-1 transition-all shadow-sm cursor-pointer"
              >
                <Plus className="w-3 h-3" />
                <span>Tambah Item</span>
              </button>
            </div>

            <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
              {lineItems.map((item, idx) => (
                <div key={item.id} className="grid grid-cols-12 gap-1.5 items-center p-2 bg-slate-50 border border-slate-200 rounded-xl text-xs">
                  <div className="col-span-4">
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => handleUpdateLineItem(idx, 'description', e.target.value)}
                      placeholder="Deskripsi item / perkhidmatan..."
                      className="w-full px-2.5 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-900 font-medium"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      min={1}
                      value={item.quantity}
                      onChange={(e) => handleUpdateLineItem(idx, 'quantity', Number(e.target.value))}
                      placeholder="Qty"
                      className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-center font-bold text-slate-900"
                      required
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      value={item.unitPrice}
                      onChange={(e) => handleUpdateLineItem(idx, 'unitPrice', Number(e.target.value))}
                      placeholder="Harga (RM)"
                      className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-xs text-center font-mono font-bold text-slate-900"
                      required
                    />
                  </div>
                  <div className="col-span-2 text-right font-mono font-extrabold text-slate-900 px-1">
                    RM {item.amount.toFixed(2)}
                  </div>
                  <div className="col-span-2 flex items-center justify-end gap-0.5">
                    <button
                      type="button"
                      onClick={() => handleMoveLineItemUp(idx)}
                      disabled={idx === 0}
                      className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-20 disabled:cursor-not-allowed rounded-md transition-colors cursor-pointer"
                      title="Alih Ke Atas"
                    >
                      <ArrowUp className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMoveLineItemDown(idx)}
                      disabled={idx === lineItems.length - 1}
                      className="p-1 text-slate-400 hover:text-slate-800 disabled:opacity-20 disabled:cursor-not-allowed rounded-md transition-colors cursor-pointer"
                      title="Alih Ke Bawah"
                    >
                      <ArrowDown className="w-3.5 h-3.5" />
                    </button>
                    {lineItems.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveLineItem(idx)}
                        className="p-1 text-rose-600 hover:bg-rose-50 rounded-md transition-colors cursor-pointer"
                        title="Hapus Item"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Jumlah Keseluruhan (RM)</label>
              <input
                type="number"
                value={invAmount}
                onChange={(e) => setInvAmount(Number(e.target.value))}
                min={1}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-100 border border-slate-200 text-xs font-bold font-mono text-slate-900"
                readOnly
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Tarikh Akhir (Due Date)</label>
              <input
                type="date"
                value={invDueDate}
                onChange={(e) => setInvDueDate(e.target.value)}
                className="w-full px-3.5 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white text-xs text-slate-900"
                required
              />
            </div>
          </div>

          {/* Pengurusan Status & Bukti Resit Pembayaran */}
          {editingInvoice && (
            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-extrabold text-slate-800 uppercase tracking-wide flex items-center gap-1.5">
                  <Receipt className="w-3.5 h-3.5 text-asmm-red" />
                  <span>Pengesahan & Bukti Resit</span>
                </span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full font-mono ${invReceiptUrl ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-200 text-slate-700'}`}>
                  {invReceiptUrl ? 'Resit Dilampirkan' : 'Tiada Resit'}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Status Pembayaran</label>
                  <select
                    value={invStatus}
                    onChange={(e) => setInvStatus(e.target.value as any)}
                    className="w-full px-3.5 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-asmm-red text-xs font-bold text-slate-900 cursor-pointer"
                  >
                    <option value="Belum Dibayar">Belum Dibayar</option>
                    <option value="Menunggu Pengesahan">Menunggu Pengesahan</option>
                    <option value="Lulus / Dibayar">Lulus / Dibayar</option>
                    <option value="Batal">Batal</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">Bukti Resit (Gambar / PDF)</label>
                  {invReceiptUrl ? (
                    <div className="flex items-center gap-2 p-2 bg-white border border-slate-200 rounded-xl shadow-xs">
                      {/* Clickable thumbnail to view receipt */}
                      <button
                        type="button"
                        onClick={() => setViewingReceiptUrl(invReceiptUrl)}
                        className="relative group shrink-0 w-11 h-11 rounded-lg overflow-hidden border border-slate-200 cursor-pointer bg-slate-50"
                        title="Klik untuk membesarkan resit"
                      >
                        {(() => {
                          const resolved = resolveCloudinaryUrl(invReceiptUrl);
                          const isDirectImg = resolved.startsWith('data:image') || (resolved.startsWith('http') && !resolved.toLowerCase().endsWith('.pdf'));
                          return isDirectImg ? (
                            <img src={resolved} alt="Resit" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                          ) : (
                            <div className="w-full h-full bg-slate-100 text-slate-700 font-bold text-[10px] flex flex-col items-center justify-center">
                              <Receipt className="w-4 h-4 text-asmm-red" />
                              <span>PDF</span>
                            </div>
                          );
                        })()}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white">
                          <Eye className="w-4 h-4" />
                        </div>
                      </button>

                      <div className="min-w-0 flex-1">
                        <div className="text-[11px] font-bold text-emerald-700 truncate">Resit Dilampirkan</div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <button
                            type="button"
                            onClick={() => setViewingReceiptUrl(invReceiptUrl)}
                            className="inline-flex items-center gap-1 text-[11px] font-extrabold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                          >
                            <Eye className="w-3 h-3" />
                            <span>Lihat Resit</span>
                          </button>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1">
                        <label className="px-2 py-1 text-[10px] font-bold text-slate-600 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors cursor-pointer inline-flex items-center gap-1">
                          <Upload className="w-2.5 h-2.5 text-asmm-red" />
                          <span>Tukar</span>
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            className="hidden"
                            disabled={isUploadingReceipt}
                            onChange={handleReceiptFileChange}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => setInvReceiptUrl('')}
                          className="px-2 py-0.5 text-[10px] font-bold text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                          title="Padam resit ini"
                        >
                          Padam
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label className={`flex items-center justify-center gap-2 px-3 py-2.5 bg-white hover:bg-slate-50 border border-dashed ${isUploadingReceipt ? 'border-amber-400 bg-amber-50/40 text-amber-700' : 'border-slate-300 hover:border-asmm-red text-slate-600'} rounded-xl text-xs font-bold cursor-pointer transition-all`}>
                      {isUploadingReceipt ? (
                        <>
                          <RefreshCw className="w-3.5 h-3.5 text-amber-600 animate-spin" />
                          <span>Memuat naik resit...</span>
                        </>
                      ) : (
                        <>
                          <Upload className="w-3.5 h-3.5 text-asmm-red" />
                          <span>Muat Naik Resit (Imej / PDF)</span>
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            className="hidden"
                            disabled={isUploadingReceipt}
                            onChange={handleReceiptFileChange}
                          />
                        </>
                      )}
                    </label>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="pt-2 flex justify-end gap-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setIsCreateModalOpen(false)}
              className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-xl"
            >
              Batal
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white font-extrabold text-xs rounded-xl transition-all shadow-sm"
            >
              {editingInvoice ? 'Kemaskini & Simpan Invois' : 'Jana & Simpan Invois'}
            </button>
          </div>
        </form>
      </Modal>

      {/* PENGESAHAN PENERIMAAN BAYARAN MODAL (Custom modal element, zero native popup) */}
      <Modal
        isOpen={Boolean(confirmingInvoice)}
        onClose={() => !isSubmittingSahkan && setConfirmingInvoice(null)}
        title="Pengesahan Penerimaan Bayaran Invois"
      >
        {confirmingInvoice && (
          <div className="space-y-4">
            <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-950 space-y-1.5">
              <div className="flex items-center gap-2 text-emerald-800 font-extrabold text-sm">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                <span>Sahkan Bayaran Telah Diterima</span>
              </div>
              <p className="text-xs text-emerald-900 leading-relaxed">
                Anda sedang mengesahkan bahawa bayaran bagi invois ini telah selamat dikreditkan ke dalam akaun rasmi{' '}
                <strong>
                  {getInvoiceBankAccountKey(confirmingInvoice) === 'persatuan'
                    ? 'Persatuan (Bank Islam Induk)'
                    : confirmingInvoice.gelanggang || 'Gelanggang'}
                </strong>.
              </p>
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-2.5 text-xs">
              <div className="flex justify-between items-center py-1 border-b border-slate-200">
                <span className="text-slate-500 font-semibold">No. Invois:</span>
                <span className="font-mono font-bold text-slate-900">{confirmingInvoice.invoiceNo}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-200">
                <span className="text-slate-500 font-semibold">Nama Pembayar:</span>
                <span className="font-bold text-slate-900">{confirmingInvoice.memberName}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-200">
                <span className="text-slate-500 font-semibold">Tujuan / Butiran:</span>
                <span className="font-medium text-slate-800 text-right max-w-[240px] truncate">{confirmingInvoice.title}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-200">
                <span className="text-slate-500 font-semibold">Gelanggang Sah:</span>
                <span className="font-bold text-slate-800">{confirmingInvoice.gelanggang}</span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-200">
                <span className="text-slate-500 font-semibold">Kaedah / Saluran Bayaran:</span>
                <span className="font-bold text-blue-700">
                  {getInvoiceBankAccountKey(confirmingInvoice) === 'persatuan'
                    ? 'Akaun Induk Persatuan'
                    : `Akaun Gelanggang (${confirmingInvoice.gelanggang})`}
                </span>
              </div>
              <div className="flex justify-between items-center py-1 border-b border-slate-200">
                <span className="text-slate-500 font-semibold">Pegawai Mengesahkan:</span>
                <span className="font-bold text-slate-900">
                  {currentUser?.name} ({currentUser?.role})
                </span>
              </div>
              <div className="flex justify-between items-center pt-1.5">
                <span className="text-slate-700 font-extrabold text-sm">Jumlah Bayaran Disahkan:</span>
                <span className="text-lg font-black font-mono text-emerald-600">
                  RM {confirmingInvoice.amount}.00
                </span>
              </div>
            </div>

            {confirmingInvoice.receiptUrl && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl flex items-center justify-between gap-2 text-xs">
                <span className="text-blue-900 font-medium truncate">Bukti Slip / Resit Disediakan Pembayar</span>
                <button
                  type="button"
                  onClick={() => setViewingReceiptUrl(confirmingInvoice.receiptUrl || null)}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs flex items-center gap-1 shrink-0 cursor-pointer"
                >
                  <Eye className="w-3 h-3" />
                  <span>Lihat Slip</span>
                </button>
              </div>
            )}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                disabled={isSubmittingSahkan}
                onClick={() => setConfirmingInvoice(null)}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-all cursor-pointer disabled:opacity-50"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={isSubmittingSahkan}
                onClick={() => handleExecuteSahkanBayaran(confirmingInvoice)}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs rounded-xl transition-all shadow-md flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {isSubmittingSahkan ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Mengesahkan...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-emerald-200" />
                    <span>Sahkan Penerimaan Bayaran</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* MODAL PAPARAN BUKTI RESIT / SLIP BAYARAN */}
      <ReceiptViewerModal
        isOpen={Boolean(viewingReceiptUrl)}
        onClose={() => setViewingReceiptUrl(null)}
        receiptUrl={viewingReceiptUrl}
        title="Paparan Bukti Resit Bayaran"
        invoiceNo={editingInvoice?.invoiceNo || undefined}
      />





      {/* SINGLE BILL PDF PRINT CONTAINER (Strictly Single Document Output Mounted to document.body) */}
      {typeof document !== 'undefined' && (receiptData || printInvoiceData) && createPortal(
        <div className="print-document-portal hidden print:block font-sans text-slate-900 relative w-full bg-white">
          {/* 45-Degree Slanted Corner Ribbon — flush with page edge */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none z-50 print-ribbon-clip">
            <div
              className="absolute top-10 -right-24 w-[380px] py-2.5 transform rotate-45 text-center text-[11px] font-black tracking-widest text-white uppercase shadow-md flex items-center justify-center border-y border-white/30"
              style={{
                backgroundColor: receiptData ? '#059669' : (printInvoiceData?.isPaid ? '#059669' : '#B91C1C'),
                color: '#ffffff',
                WebkitPrintColorAdjust: 'exact',
                printColorAdjust: 'exact'
              }}
            >
              <span>{receiptData ? 'DIBAYAR / PAID' : (printInvoiceData?.isPaid ? 'DIBAYAR / PAID' : 'BELUM DIBAYAR')}</span>
            </div>
          </div>

          {receiptData && (
            <div className="space-y-6 max-w-3xl mx-auto p-8 pt-6">
              {/* Header Format Surat Rasmi */}
              <div className="relative pb-4 border-b-4 border-double border-slate-900 pt-2 pr-28">
                <div className="flex items-center gap-4">
                  <img src="/logo-pssasmm.png" alt="Logo PSSASMM" className="w-20 h-20 object-contain shrink-0" />
                  <div className="space-y-1 text-left min-w-0">
                    <h1 className="text-base font-black tracking-tight text-slate-900 uppercase leading-tight">
                      PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA KEDAH
                    </h1>
                    <div className="text-xs font-extrabold text-asmm-red uppercase tracking-wider font-mono">
                      PENDAFTARAN ROS: PPM-007-02-28082022
                    </div>
                    <p className="text-xs text-slate-600 leading-tight">
                      263, Persiaran Utama 1/9, Kulim Utama, 09000 Kulim, Kedah Darul Aman
                    </p>
                    <p className="text-xs text-slate-500 font-mono">
                      Tel: 012-4475157 • Emel: rasmi@asmm.org.my • Portal: asmm.pages.dev
                    </p>
                  </div>
                </div>
              </div>

              {/* Title Header */}
              <div className="text-center space-y-1 py-2">
                <h2 className="text-lg font-black text-slate-900 uppercase tracking-wide">
                  RESIT RASMI PEMBAYARAN PSSASMM
                </h2>
                <p className="text-xs font-mono text-slate-500">No. Rujukan: {receiptData.invoiceNo} • Tarikh: {receiptData.date}</p>
              </div>

              {/* Recipient Info Block */}
              <div className="text-xs border border-slate-300 p-3.5 rounded-xl bg-slate-50 mb-3 space-y-1">
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                  PEMBAYAR / DITERBITKAN KEPADA:
                </div>
                <div className="font-extrabold text-slate-900 text-sm">{receiptData.payorName}</div>
                <div className="text-slate-700 font-medium">
                  {receiptData.payorAddress || `Gelanggang ${receiptData.gelanggang}, Kedah Darul Aman`}
                </div>
                <div className="font-mono text-slate-700">
                  Tel / WhatsApp: {receiptData.payorPhone || '-'}
                </div>
                {receiptData.payorEmail && (
                  <div className="text-slate-600 font-mono">Emel: {receiptData.payorEmail}</div>
                )}
              </div>

              {/* Particulars & Itemized Table */}
              <div className="space-y-3">
                <table className="w-full text-xs border-collapse border border-slate-300">
                  <tbody>
                    <tr className="border-b border-slate-300 bg-slate-50">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600 w-1/3">No. Invois / Dokumen:</td>
                      <td className="p-2 border border-slate-300 font-mono font-bold text-slate-900">{receiptData.invoiceNo}</td>
                    </tr>
                    <tr className="border-b border-slate-300">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600">Gelanggang Sah:</td>
                      <td className="p-2 border border-slate-300 font-bold text-asmm-red">{receiptData.gelanggang}</td>
                    </tr>
                    <tr className="border-b border-slate-300 bg-slate-50">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600">Perkara / Kategori:</td>
                      <td className="p-2 border border-slate-300 font-bold text-slate-900">{receiptData.title}</td>
                    </tr>
                    <tr className="border-b border-slate-300">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600">Status Pembayaran:</td>
                      <td className="p-2 border border-slate-300 font-black text-emerald-700">Disahkan (Lulus / Dibayar)</td>
                    </tr>
                  </tbody>
                </table>

                {/* Line Items Detail Table */}
                <table className="w-full text-xs border-collapse border border-slate-300">
                  <thead>
                    <tr className="bg-slate-900 text-white font-extrabold text-[11px]">
                      <th className="p-2 border border-slate-700 text-center w-10">#</th>
                      <th className="p-2 border border-slate-700 text-left">Perihal Item / Perkhidmatan</th>
                      <th className="p-2 border border-slate-700 text-center w-16">Kuantiti</th>
                      <th className="p-2 border border-slate-700 text-right w-24">Harga (RM)</th>
                      <th className="p-2 border border-slate-700 text-right w-28">Jumlah (RM)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(receiptData.items && receiptData.items.length > 0 ? receiptData.items : [
                      { id: '1', description: receiptData.title, quantity: 1, unitPrice: receiptData.amount, amount: receiptData.amount }
                    ]).map((it: any, idx: number) => (
                      <tr key={it.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="p-2 border border-slate-300 text-center font-mono">{idx + 1}</td>
                        <td className="p-2 border border-slate-300 font-bold text-slate-900">{it.description}</td>
                        <td className="p-2 border border-slate-300 text-center font-mono font-bold">{it.quantity}</td>
                        <td className="p-2 border border-slate-300 text-right font-mono">RM {Number(it.unitPrice).toFixed(2)}</td>
                        <td className="p-2 border border-slate-300 text-right font-mono font-bold text-slate-900">RM {Number(it.amount).toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-100 font-extrabold text-xs border-t-2 border-slate-900">
                      <td colSpan={4} className="p-2 text-slate-900 text-right border border-slate-300 uppercase tracking-wider">
                        JUMLAH INVOIS (RM):
                      </td>
                      <td className="p-2 font-mono text-slate-900 text-right border border-slate-300">
                        RM {Number(receiptData.amount).toFixed(2)}
                      </td>
                    </tr>
                    <tr className="bg-emerald-50 font-extrabold text-xs">
                      <td colSpan={4} className="p-2 text-emerald-900 text-right border border-slate-300 uppercase tracking-wider">
                        JUMLAH DIBAYAR (RM):
                      </td>
                      <td className="p-2 font-mono text-emerald-800 text-right border border-slate-300 font-black">
                        RM {Number(receiptData.amount).toFixed(2)}
                      </td>
                    </tr>
                    <tr className="bg-slate-200/80 font-black text-xs">
                      <td colSpan={4} className="p-2 text-slate-900 text-right border border-slate-300 uppercase tracking-wider">
                        BAKI / IMBANGAN (RM):
                      </td>
                      <td className="p-2 font-mono text-emerald-700 text-right border border-slate-300 text-xs">
                        RM 0.00
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* FPX Audit Record Table (Positioned BELOW Itemized Breakdown) */}
                <div className="mt-4 pt-2">
                  <div className="text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1">
                    REKOD AUDIT TRANSAKSI FPX:
                  </div>
                  <table className="w-full text-xs border-collapse border border-slate-300">
                    <tbody>
                      <tr className="border-b border-slate-300 bg-slate-50">
                        <td className="p-2 border border-slate-300 font-bold text-slate-600 w-1/3">Jenis / Saluran Transaksi:</td>
                        <td className="p-2 border border-slate-300 font-bold text-slate-900">{receiptData.paymentMethod || 'ToyyibPay FPX Online Banking'}</td>
                      </tr>
                      <tr className="border-b border-slate-300">
                        <td className="p-2 border border-slate-300 font-bold text-slate-600">Kod Transaksi FPX / Bill Code:</td>
                        <td className="p-2 border border-slate-300 font-mono font-extrabold text-emerald-800">{receiptData.billCode || 'ASMM-FPX-LOCAL'}</td>
                      </tr>
                      <tr className="border-b border-slate-300 bg-slate-50">
                        <td className="p-2 border border-slate-300 font-bold text-slate-600">Tarikh & Masa Pembayaran:</td>
                        <td className="p-2 border border-slate-300 font-mono font-bold text-slate-900">{receiptData.date} • {receiptData.time || '14:45:00 PM'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="pt-6 text-[10px] text-slate-400 text-center border-t border-slate-200">
                Ini adalah cetakan dokumen komputer rasmi. Tiada tanda tangan fizikal diperlukan. (ROS: PPM-007-02-28082022)
              </div>
            </div>
          )}

          {!receiptData && printInvoiceData && (
            <div className="space-y-6 max-w-3xl mx-auto p-8 pt-6">
              {/* Top Modal Actions Bar (No Print) */}
              <div className="flex items-center justify-between gap-3 p-4 bg-slate-900 text-white rounded-2xl no-print shadow-md">
                <div className="flex items-center gap-2.5">
                  <FileText className="w-5 h-5 text-amber-400 shrink-0" />
                  <div>
                    <div className="font-extrabold text-xs">Invois {printInvoiceData.invoice.invoiceNo}</div>
                    <div className="text-[10px] text-slate-300">
                      {printInvoiceData.isPaid ? 'Status: Telah Dibayar' : 'Status: Belum Dibayar (FPX ToyyibPay)'}
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {!printInvoiceData.isPaid && (
                    <button
                      onClick={() => handlePayInvoice(printInvoiceData.invoice)}
                      disabled={isProcessingId === printInvoiceData.invoice.id}
                      className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-black text-xs rounded-xl transition-all shadow-md flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                    >
                      <CreditCard className="w-4 h-4" />
                      <span>{isProcessingId === printInvoiceData.invoice.id ? 'Menjana FPX...' : 'Bayar FPX Sekarang'}</span>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      const link = `${window.location.origin}/payments?inv=${printInvoiceData.invoice.invoiceNo}`;
                      navigator.clipboard.writeText(link);
                      showToast('success', 'Pautan Invois Disalin!', `Pautan terus invois sedia dikongsi.`);
                    }}
                    className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs rounded-xl transition-all flex items-center gap-1.5 cursor-pointer border border-slate-700"
                  >
                    <Share2 className="w-3.5 h-3.5 text-amber-400" />
                    <span>Salin Pautan</span>
                  </button>

                  <button
                    onClick={() => window.print()}
                    className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs rounded-xl transition-all flex items-center gap-1.5 cursor-pointer border border-slate-700"
                  >
                    <Printer className="w-3.5 h-3.5" />
                    <span>Cetak</span>
                  </button>

                  <button
                    onClick={() => setPrintInvoiceData(null)}
                    className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
                    title="Tutup Modal"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Header Format Surat Rasmi */}
              <div className="relative pb-4 border-b-4 border-double border-slate-900 pt-2 pr-28">
                <div className="flex items-center gap-4">
                  <img src="/logo-pssasmm.png" alt="Logo PSSASMM" className="w-20 h-20 object-contain shrink-0" />
                  <div className="space-y-1 text-left min-w-0">
                    <h1 className="text-base font-black tracking-tight text-slate-900 uppercase leading-tight">
                      PERSATUAN SENI SILAT AWANG SULUNG MERAH MUDA KEDAH
                    </h1>
                    <div className="text-xs font-extrabold text-asmm-red uppercase tracking-wider font-mono">
                      PENDAFTARAN ROS: PPM-007-02-28082022
                    </div>
                    <p className="text-xs text-slate-600 leading-tight">
                      263, Persiaran Utama 1/9, Kulim Utama, 09000 Kulim, Kedah Darul Aman
                    </p>
                    <p className="text-xs text-slate-500 font-mono">
                      Tel: 012-4475157 • Emel: rasmi@asmm.org.my • Portal: asmm.pages.dev
                    </p>
                  </div>
                </div>
              </div>

              {/* Title Header */}
              <div className="text-center space-y-1 py-2">
                <h2 className="text-lg font-black text-slate-900 uppercase tracking-wide">
                  INVOIS RASMI PSSASMM
                </h2>
                <p className="text-xs font-mono text-slate-500">
                  No. Rujukan: {printInvoiceData.invoice.invoiceNo} • Tarikh Terbit: {printInvoiceData.invoice.createdAt || printInvoiceData.invoice.dueDate}
                </p>
              </div>

              {/* Recipient (Bill To) Info Block */}
              <div className="text-xs border border-slate-300 p-3.5 rounded-xl bg-slate-50 mb-3 space-y-1">
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">
                  {printInvoiceData.invoice.payerType === 'Vendor / Pihak Ketiga' || printInvoiceData.invoice.memberId?.startsWith('VENDOR-')
                    ? 'DITERBITKAN KEPADA (VENDOR / SYARIKAT):'
                    : 'DITERBITKAN KEPADA (AHLI / PESILAT):'}
                </div>
                <div className="font-extrabold text-slate-900 text-sm">{printInvoiceData.invoice.memberName}</div>
                <div className="text-slate-700 font-medium">
                  {printInvoiceData.invoice.vendorAddress || printInvoiceData.invoice.address || `Gelanggang ${printInvoiceData.invoice.gelanggang}, Kedah Darul Aman`}
                </div>
                <div className="font-mono text-slate-700">
                  Tel / WhatsApp: {printInvoiceData.invoice.vendorPhone || printInvoiceData.invoice.memberPhone || '-'}
                </div>
                {printInvoiceData.invoice.vendorEmail && (
                  <div className="text-slate-600 font-mono">Emel: {printInvoiceData.invoice.vendorEmail}</div>
                )}
              </div>

              {/* Particulars & Itemized Table */}
              <div className="space-y-3">
                <table className="w-full text-xs border-collapse border border-slate-300">
                  <tbody>
                    <tr className="border-b border-slate-300 bg-slate-50">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600 w-1/3">No. Invois:</td>
                      <td className="p-2 border border-slate-300 font-mono font-bold text-slate-900">{printInvoiceData.invoice.invoiceNo}</td>
                    </tr>
                    <tr className="border-b border-slate-300">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600">Gelanggang Sah:</td>
                      <td className="p-2 border border-slate-300 font-bold text-asmm-red">{printInvoiceData.invoice.gelanggang}</td>
                    </tr>
                    <tr className="border-b border-slate-300 bg-slate-50">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600">Kategori Invois:</td>
                      <td className="p-2 border border-slate-300 font-bold text-slate-900">{printInvoiceData.invoice.category}</td>
                    </tr>
                    <tr className="border-b border-slate-300">
                      <td className="p-2 border border-slate-300 font-bold text-slate-600">Status FPX:</td>
                      <td className={`p-2 border border-slate-300 font-black ${printInvoiceData.isPaid ? 'text-emerald-700' : 'text-asmm-red'}`}>
                        {printInvoiceData.invoice.status}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Line Items Detail Table */}
                <table className="w-full text-xs border-collapse border border-slate-300">
                  <thead>
                    <tr className="bg-slate-900 text-white font-extrabold text-[11px]">
                      <th className="p-2 border border-slate-700 text-center w-10">#</th>
                      <th className="p-2 border border-slate-700 text-left">Perihal Item / Perkhidmatan</th>
                      <th className="p-2 border border-slate-700 text-center w-16">Kuantiti</th>
                      <th className="p-2 border border-slate-700 text-right w-24">Harga (RM)</th>
                      <th className="p-2 border border-slate-700 text-right w-28">Jumlah (RM)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(printInvoiceData.invoice.items && printInvoiceData.invoice.items.length > 0 ? printInvoiceData.invoice.items : [
                      { id: '1', description: printInvoiceData.invoice.title, quantity: 1, unitPrice: printInvoiceData.invoice.amount, amount: printInvoiceData.invoice.amount }
                    ]).map((it, idx) => (
                      <tr key={it.id || idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                        <td className="p-2 border border-slate-300 text-center font-mono">{idx + 1}</td>
                        <td className="p-2 border border-slate-300 font-bold text-slate-900">{it.description}</td>
                        <td className="p-2 border border-slate-300 text-center font-mono font-bold">{it.quantity}</td>
                        <td className="p-2 border border-slate-300 text-right font-mono">RM {Number(it.unitPrice).toFixed(2)}</td>
                        <td className="p-2 border border-slate-300 text-right font-mono font-bold text-slate-900">RM {Number(it.amount).toFixed(2)}</td>
                      </tr>
                    ))}
                    <tr className="bg-slate-100 font-extrabold text-xs border-t-2 border-slate-900">
                      <td colSpan={4} className="p-2 text-slate-900 text-right border border-slate-300 uppercase tracking-wider">
                        JUMLAH INVOIS (RM):
                      </td>
                      <td className="p-2 font-mono text-slate-900 text-right border border-slate-300">
                        RM {Number(printInvoiceData.invoice.amount).toFixed(2)}
                      </td>
                    </tr>
                    <tr className={`font-extrabold text-xs ${printInvoiceData.isPaid ? 'bg-emerald-50 text-emerald-900' : 'bg-slate-50 text-slate-700'}`}>
                      <td colSpan={4} className="p-2 text-right border border-slate-300 uppercase tracking-wider">
                        JUMLAH DIBAYAR (RM):
                      </td>
                      <td className="p-2 font-mono text-right border border-slate-300 font-black">
                        RM {printInvoiceData.isPaid ? Number(printInvoiceData.invoice.amount).toFixed(2) : '0.00'}
                      </td>
                    </tr>
                    <tr className={`font-black text-xs ${printInvoiceData.isPaid ? 'bg-slate-200/80 text-slate-900' : 'bg-red-50 text-asmm-red'}`}>
                      <td colSpan={4} className="p-2 text-right border border-slate-300 uppercase tracking-wider">
                        BAKI TERTUNGGAK (RM):
                      </td>
                      <td className="p-2 font-mono text-right border border-slate-300 text-xs">
                        {printInvoiceData.isPaid ? 'RM 0.00' : `RM ${Number(printInvoiceData.invoice.amount).toFixed(2)}`}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* FPX Audit Record Table (Positioned BELOW Itemized Breakdown if paid) */}
                {(printInvoiceData.isPaid || printInvoiceData.invoice.status === 'Lulus / Dibayar') && (
                  <div className="mt-4 pt-2">
                    <div className="text-[10px] font-black text-slate-500 uppercase tracking-wider mb-1">
                      REKOD AUDIT TRANSAKSI FPX:
                    </div>
                    <table className="w-full text-xs border-collapse border border-slate-300">
                      <tbody>
                        <tr className="border-b border-slate-300 bg-slate-50">
                          <td className="p-2 border border-slate-300 font-bold text-slate-600 w-1/3">Jenis / Saluran Transaksi:</td>
                          <td className="p-2 border border-slate-300 font-bold text-slate-900">{printInvoiceData.invoice.paymentMethod || 'ToyyibPay FPX Online Banking'}</td>
                        </tr>
                        <tr className="border-b border-slate-300">
                          <td className="p-2 border border-slate-300 font-bold text-slate-600">Kod Transaksi FPX / Bill Code:</td>
                          <td className="p-2 border border-slate-300 font-mono font-extrabold text-emerald-800">{printInvoiceData.invoice.toyibBillCode || 'ASMM-FPX-LOCAL'}</td>
                        </tr>
                        <tr className="border-b border-slate-300 bg-slate-50">
                          <td className="p-2 border border-slate-300 font-bold text-slate-600">Tarikh & Masa Pembayaran:</td>
                          <td className="p-2 border border-slate-300 font-mono font-bold text-slate-900">
                            {printInvoiceData.invoice.paidAt?.split(' ')[0] || printInvoiceData.invoice.createdAt} • {printInvoiceData.invoice.paidAt?.includes(' ') ? printInvoiceData.invoice.paidAt.split(' ')[1] : '14:45:00 PM'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="pt-6 text-[10px] text-slate-400 text-center border-t border-slate-200">
                Ini adalah cetakan dokumen komputer rasmi. Tiada tanda tangan fizikal diperlukan. (ROS: PPM-007-02-28082022)
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};
